import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useSearchParams } from 'react-router-dom';
import Drawer from '../components/Drawer';
import {
  Truck,
  MapPin,
  Clock,
  CheckCircle,
  AlertCircle,
  Package,
  Phone,
  Navigation,
  Send,
  Plus,
  X,
  ChevronRight,
  ChevronDown,
  Calendar,
  User,
  FileText,
  Play,
  Eye,
  RefreshCw,
  Map,
  Loader2,
  CheckSquare,
  Square,
  Info,
  ExternalLink,
  MessageSquare,
  Radio,
  GitBranch,
  Trash2,
  MoreHorizontal,
  Check,
  Home,
  GripVertical,
  Sparkles,
  ArrowUpDown,
  Hand
} from 'lucide-react';
import { supabase } from '../supabaseClient';
import { UNIT_TERMINAL_STATUSES, RECEIPT_TERMINAL_STATUSES, ITEM_TERMINAL_STATUSES, ITEM_STAGED_STATUSES } from '../utils/statusConstants';
import DriverLocationMap from './DriverLocationMap';

// Language options for driver dispatch (kept inline to avoid import dependency)
const DRIVER_LANGUAGES = [
  { code: 'en', label: 'English', flag: '🇺🇸' },
  { code: 'es', label: 'Español', flag: '🇪🇸' },
  { code: 'pt', label: 'Português', flag: '🇧🇷' },
  { code: 'zh', label: '中文', flag: '🇨🇳' },
  { code: 'vi', label: 'Tiếng Việt', flag: '🇻🇳' },
  { code: 'ko', label: '한국어', flag: '🇰🇷' },
  { code: 'ar', label: 'العربية', flag: '🇵🇸' },
  { code: 'fr', label: 'Français', flag: '🇫🇷' },
  { code: 'hi', label: 'हिन्दी', flag: '🇮🇳' },
  { code: 'tl', label: 'Filipino', flag: '🇵🇭' },
  { code: 'ru', label: 'Русский', flag: '🇷🇺' },
  { code: 'de', label: 'Deutsch', flag: '🇩🇪' },
  { code: 'ja', label: '日本語', flag: '🇯🇵' },
  { code: 'it', label: 'Italiano', flag: '🇮🇹' },
  { code: 'pl', label: 'Polski', flag: '🇵🇱' },
  { code: 'uk', label: 'Українська', flag: '🇺🇦' },
  { code: 'tr', label: 'Türkçe', flag: '🇹🇷' },
  { code: 'th', label: 'ไทย', flag: '🇹🇭' },
  { code: 'my', label: 'မြန်မာ', flag: '🇲🇲' },
  { code: 'am', label: 'አማርኛ', flag: '🇪🇹' },
];

// Phone helpers for the saved-drivers dropdown. Dedupe is done on digits only
// so "(415) 624-6941" and "4156246941" resolve to the same driver.
const normalizePhone = (phone) => (phone || '').replace(/\D/g, '');
const formatPhoneDisplay = (phone) => {
  const digits = normalizePhone(phone);
  if (digits.length === 10) return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  if (digits.length === 11 && digits.startsWith('1')) return `(${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  return phone || '';
};

const DeliveriesTab = ({ storeId, store, receipts = [], onRefresh, hideHeader = false, onOpenSettings, addNotification }) => {
  // URL state persistence
  const [searchParams, setSearchParams] = useSearchParams();
  
  // State
  const [activeSection, setActiveSection] = useState(() => {
    const section = searchParams.get('section');
    return (section === 'ready' || section === 'routes') ? section : 'ready';
  });
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creatingRoute, setCreatingRoute] = useState(false);
  const [optimizingRoute, setOptimizingRoute] = useState(false);
  const [showCreateRouteModal, setShowCreateRouteModal] = useState(false);
  const [showDispatchModal, setShowDispatchModal] = useState(false);
  const [selectedRoute, setSelectedRoute] = useState(null);
  const [driverInfo, setDriverInfo] = useState({ name: '', phone: '', language: 'en' });
  // Drivers derived from past dispatches (delivery_routes.driver_*), deduped by phone
  const [savedDrivers, setSavedDrivers] = useState([]);
  const [optimizedOrder, setOptimizedOrder] = useState([]);
  const [routeName, setRouteName] = useState('');
  const [inventoryUnits, setInventoryUnits] = useState([]);
  // Map of receipt_item_id -> item_statuses.status. Lets calculateReadiness honor
  // items marked ready/delivered that have NO inventory unit (the "unitless" case),
  // matching the order-detail view's logic. Without this, such items always fall
  // through to "waiting" here even though the detail view shows them "ready".
  const [itemStatusMap, setItemStatusMap] = useState({});
  const [showDriverMap, setShowDriverMap] = useState(false);
  const [deletingRouteId, setDeletingRouteId] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  
  // New state for route management
  const [routeFilter, setRouteFilter] = useState('active'); // 'active', 'completed', 'all'
  const [showCleanupModal, setShowCleanupModal] = useState(false);
  const [cleaningUp, setCleaningUp] = useState(false);
  const [dispatching, setDispatching] = useState(false);
  const [selectedRoutesForDelete, setSelectedRoutesForDelete] = useState([]);
  
  // State for partial delivery routes (single-stop planned routes that should show in Ready)
  const [selectedPartialRoutes, setSelectedPartialRoutes] = useState([]);

  // Store location state for route optimization (loaded from store_addresses)
  const [storeLocation, setStoreLocation] = useState({
    address: '',
    latitude: null,
    longitude: null
  });

  // NEW: Manual route ordering state
  const [routeOrderMode, setRouteOrderMode] = useState('auto'); // 'auto' or 'manual'
  const [manualOrder, setManualOrder] = useState([]);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const dragNodeRef = useRef(null);

  // NEW: Edit existing route order state
  const [showEditRouteModal, setShowEditRouteModal] = useState(false);
  const [editingRoute, setEditingRoute] = useState(null);
  const [editingStops, setEditingStops] = useState([]);
  const [savingRouteOrder, setSavingRouteOrder] = useState(false);

  // Sync activeSection to URL for refresh persistence
  useEffect(() => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set('section', activeSection);
    setSearchParams(newParams, { replace: true });
  }, [activeSection]);

  // Load routes and inventory on mount
  useEffect(() => {
    loadRoutes();
    loadInventoryUnits();
    loadItemStatuses();
  }, [storeId]);

  // Keep item statuses in sync when the receipts list changes (parent refetch,
  // new order, status change upstream). Keyed on the id set so it doesn't refire
  // on unrelated receipt field changes.
  const receiptIdsKey = (receipts || []).map(r => r.id).filter(Boolean).join(',');
  useEffect(() => {
    loadItemStatuses();
  }, [receiptIdsKey]);

  // Subscribe to relevant table updates for real-time sync
  // FIXED: Filter by storeId, removed onRefresh from deps to prevent loop
  useEffect(() => {
    if (!storeId) return;
    
    console.log('🔌 Setting up real-time subscriptions for DeliveriesTab...');
    
    const channel = supabase
      .channel(`delivery-tab-${storeId}`)
      // Route changes - filtered by store
      .on('postgres_changes',
        { event: '*', schema: 'public', table: 'delivery_routes', filter: `store_id=eq.${storeId}` },
        (payload) => {
          console.log('🔄 Route changed:', payload.eventType);
          loadRoutes();
        }
      )
      // Route stop changes - can't filter directly, but routes are filtered
      .on('postgres_changes',
        { event: '*', schema: 'public', table: 'delivery_route_stops' },
        (payload) => {
          console.log('🔄 Route stop changed:', payload.eventType);
          loadRoutes();
        }
      )
      // Inventory changes - filtered by store
      .on('postgres_changes',
        { event: '*', schema: 'public', table: 'inventory_units', filter: `store_id=eq.${storeId}` },
        (payload) => {
          console.log('🔄 Inventory changed:', payload.eventType);
          loadInventoryUnits();
          loadItemStatuses();
        }
      )
      // Item status changes (Mark Ready / Delivered / Picked Up on unitless items).
      // No store_id column on item_statuses, so we can't filter here; loadItemStatuses
      // re-scopes to the in-view receipt ids itself.
      .on('postgres_changes',
        { event: '*', schema: 'public', table: 'item_statuses' },
        (payload) => {
          console.log('🔄 Item status changed:', payload.eventType);
          loadItemStatuses();
        }
      )
      .subscribe((status) => {
        console.log('📡 DeliveriesTab subscription status:', status);
        if (status === 'CHANNEL_ERROR') {
          console.error('❌ Realtime subscription failed - will rely on polling');
        }
      });

    // BACKUP: Polling interval in case realtime fails (every 30 seconds - reduced frequency)
    const pollInterval = setInterval(() => {
      loadRoutes();
      loadInventoryUnits();
      loadItemStatuses();
    }, 30000);

    return () => {
      console.log('🔌 Cleaning up DeliveriesTab subscriptions');
      supabase.removeChannel(channel);
      clearInterval(pollInterval);
    };
  }, [storeId]);

  // Load store location when store changes
  useEffect(() => {
    if (store) {
      setStoreLocation({
        address: store.address || store.store_address || '',
        latitude: store.latitude || null,
        longitude: store.longitude || null
      });
    } else if (storeId) {
      loadStoreLocation();
    }
  }, [store, storeId]);

  // NEW: Initialize manual order when create route modal opens
  useEffect(() => {
    if (showCreateRouteModal) {
      setManualOrder([...selectedOrders]);
      setRouteOrderMode('auto');
    }
  }, [showCreateRouteModal, selectedOrders]);

  const loadStoreLocation = async () => {
    if (!storeId) return;
    
    try {
      // First try to get the primary address from store_addresses
      const { data: addressData, error: addressError } = await supabase
        .from('store_addresses')
        .select('address, latitude, longitude')
        .eq('store_id', storeId)
        .eq('is_primary', true)
        .single();
      
      if (!addressError && addressData) {
        setStoreLocation({
          address: addressData.address || '',
          latitude: addressData.latitude || null,
          longitude: addressData.longitude || null
        });
        return;
      }
      
      // Fallback to stores table for backwards compatibility
      const { data, error } = await supabase
        .from('stores')
        .select('address, store_address, latitude, longitude')
        .eq('id', storeId)
        .single();
      
      if (!error && data) {
        setStoreLocation({
          address: data.address || data.store_address || '',
          latitude: data.latitude || null,
          longitude: data.longitude || null
        });
      }
    } catch (err) {
      console.error('Error loading store location:', err);
    }
  };

  const loadInventoryUnits = async () => {
    try {
      let query = supabase
        .from('inventory_units')
        .select('*');
      
      if (storeId) {
        query = query.eq('store_id', storeId);
      }
      
      const { data, error } = await query;
      
      if (!error) {
        setInventoryUnits(data || []);
      }
    } catch (err) {
      console.error('Error loading inventory units:', err);
    }
  };

  // Load item_statuses for the receipts currently in view and build a
  // receipt_item_id -> status map. item_statuses has no store_id column, so we
  // scope by the receipt ids already passed in (which are store-scoped upstream).
  //
  // Two sources, in order:
  //   1. receipt.itemStatuses — the dashboard list already carries every
  //      status row per receipt, so the tab agrees with the Orders view the
  //      moment it mounts, with no request at all.
  //   2. a fresh read for the OPEN receipts only, 100 ids per request, so
  //      the realtime item_statuses hook refreshes what matters here. The
  //      old single .in() over every receipt id broke past a few hundred
  //      receipts (URL too long), the error was swallowed, and every order
  //      then showed "waiting".
  const loadItemStatuses = async () => {
    try {
      const list = receipts || [];
      if (list.length === 0) {
        setItemStatusMap({});
        return;
      }
      const map = {};
      for (const r of list) {
        for (const row of (r.itemStatuses || [])) {
          if (row?.receipt_item_id != null) map[String(row.receipt_item_id)] = row.status;
        }
      }
      setItemStatusMap({ ...map });

      const openIds = list
        .filter(r => !RECEIPT_TERMINAL_STATUSES.includes(r.delivery_status))
        .map(r => r.id)
        .filter(Boolean);
      const CHUNK = 100;
      for (let i = 0; i < openIds.length; i += CHUNK) {
        const { data, error } = await supabase
          .from('item_statuses')
          .select('receipt_item_id, status')
          .in('receipt_id', openIds.slice(i, i + CHUNK));
        if (error) {
          console.error('Error loading item statuses:', error);
          return; // keep what the receipts gave us
        }
        for (const row of data || []) {
          if (row.receipt_item_id != null) map[String(row.receipt_item_id)] = row.status;
        }
      }
      setItemStatusMap({ ...map });
    } catch (err) {
      console.error('Error loading item statuses:', err);
    }
  };

  const loadRoutes = async () => {
    try {
      let query = supabase
        .from('delivery_routes')
        .select(`
          *,
          delivery_route_stops (*)
        `)
        .order('delivery_date', { ascending: false })
        .limit(20);
      
      if (storeId) {
        query = query.eq('store_id', storeId);
      }

      const { data, error } = await query;

      if (error) {
        if (error.code === '42P01' || error.message?.includes('does not exist')) {
          setRoutes([]);
        } else {
          console.error('Error loading routes:', error);
        }
      } else {
        setRoutes(data || []);
      }
    } catch (err) {
      console.error('Error loading routes:', err);
      setRoutes([]);
    } finally {
      setLoading(false);
    }
  };

  // Load previously-used drivers from dispatch history. Every dispatch writes
  // driver_name/driver_phone/driver_language onto delivery_routes, so past
  // drivers already live in the DB — no separate table needed. We pull the
  // most recent 200 dispatched routes, dedupe by normalized phone (keeping the
  // most recent name/language per number), and cap the dropdown at 15.
  const loadSavedDrivers = async () => {
    try {
      let query = supabase
        .from('delivery_routes')
        .select('driver_name, driver_phone, driver_language, dispatched_at')
        .not('driver_phone', 'is', null)
        .neq('driver_phone', '')
        .order('dispatched_at', { ascending: false, nullsFirst: false })
        .limit(200);

      if (storeId) {
        query = query.eq('store_id', storeId);
      }

      const { data, error } = await query;
      if (error) throw error;

      const seen = new Set();
      const drivers = [];
      for (const row of data || []) {
        const key = normalizePhone(row.driver_phone);
        if (!key || seen.has(key)) continue;
        seen.add(key);
        drivers.push({
          name: row.driver_name || '',
          phone: row.driver_phone,
          language: row.driver_language || 'en'
        });
        if (drivers.length >= 15) break;
      }
      setSavedDrivers(drivers);
    } catch (err) {
      // Non-fatal: dispatch still works with manual entry
      console.error('Error loading saved drivers:', err);
    }
  };

  // Refresh the saved-drivers list each time the dispatch drawer opens, so a
  // driver used moments ago (or by another staff member) shows up immediately.
  useEffect(() => {
    if (showDispatchModal) {
      loadSavedDrivers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showDispatchModal]);


  // Calculate delivery readiness for each receipt - RETURNS READY UNITS
  // Uses receipt_item_id for precise tracking of which specific items are delivered vs ready
  // Pure calculation function - no console.log, no state closures
  const calculateReadiness = (receipt, allInventory, allAvailable, itemStatuses = {}) => {
    const items = receipt.items || [];
    if (items.length === 0) return { ready: 0, total: 0, percentage: 0, status: 'empty', readyUnits: [], deliveredCount: 0, waitingCount: 0 };

    const receiptItemIds = items.map(i => i.id).filter(Boolean);
    const allUnitsForReceipt = allInventory.filter(u => 
      u.reserved_for_receipt_id === receipt.id ||
      (u.receipt_item_id && receiptItemIds.includes(u.receipt_item_id))
    );

    let readyCount = 0;
    let deliveredCount = 0;
    let waitingCount = 0;
    let totalItems = 0;
    let readyUnits = [];
    const claimedUnitIds = new Set();

    items.forEach(item => {
      const qty = item.quantity || 1;
      totalItems += qty;
      const skuKey = item.sku?.toLowerCase().trim();
      const receiptItemId = item.id;
      
      if (receiptItemId) {
        const linkedUnit = allUnitsForReceipt.find(u => 
          u.receipt_item_id && String(u.receipt_item_id) === String(receiptItemId)
        );
        
        if (linkedUnit) {
          if (UNIT_TERMINAL_STATUSES.includes(linkedUnit.status)) {
            deliveredCount += qty;
            claimedUnitIds.add(linkedUnit.id);
            return;
          } else if (linkedUnit.status === 'out_for_delivery') {
            claimedUnitIds.add(linkedUnit.id);
            return;
          } else if (['at_store', 'in_warehouse', 'reserved'].includes(linkedUnit.status)) {
            readyCount += qty;
            readyUnits.push(linkedUnit);
            claimedUnitIds.add(linkedUnit.id);
            return;
          }
        }
      }
      
      const reservedMatchingUnits = allUnitsForReceipt.filter(u => 
        u.sku?.toLowerCase().trim() === skuKey &&
        !claimedUnitIds.has(u.id) &&
        (!u.receipt_item_id || !items.some(i => String(i.id) === String(u.receipt_item_id))) &&
        !UNIT_TERMINAL_STATUSES.includes(u.status) &&
        u.status !== 'out_for_delivery'
      );
      
      if (reservedMatchingUnits.length > 0) {
        readyCount += qty;
        readyUnits.push(reservedMatchingUnits[0]);
        claimedUnitIds.add(reservedMatchingUnits[0].id);
        return;
      }
      
      const availableMatchingUnits = allAvailable.filter(u =>
        u.sku?.toLowerCase().trim() === skuKey &&
        !claimedUnitIds.has(u.id)
      );
      
      if (availableMatchingUnits.length > 0) {
        readyCount += qty;
        readyUnits.push(availableMatchingUnits[0]);
        claimedUnitIds.add(availableMatchingUnits[0].id);
        return;
      }

      // ── UNITLESS FALLBACK ──────────────────────────────────────────
      // No inventory unit matched this item. Before calling it "waiting",
      // honor an item_statuses row — an item can be marked ready/delivered
      // WITHOUT a physical unit (no-SKU items, or items marked ready when no
      // stock existed). This mirrors getItemStatusByInventory in the order
      // detail view, which is why that view shows "ready" while this tab
      // previously showed "waiting" for the same order.
      if (receiptItemId) {
        const fallbackStatus = itemStatuses[String(receiptItemId)];
        if (fallbackStatus) {
          if (UNIT_TERMINAL_STATUSES.includes(fallbackStatus)) {
            // delivered / picked_up
            deliveredCount += qty;
            return;
          }
          if (fallbackStatus === 'out_for_delivery') {
            // on the truck — not waiting, not selectable as ready
            return;
          }
          if (['at_store', 'in_warehouse', 'reserved'].includes(fallbackStatus)) {
            readyCount += qty;
            return;
          }
        }
      }

      waitingCount += qty;
    });

    const remainingItems = totalItems - deliveredCount;
    const percentage = remainingItems > 0 ? Math.round((readyCount / remainingItems) * 100) : 100;
    
    let status = 'waiting';
    if (remainingItems === 0) {
      status = 'all_delivered';
    } else if (waitingCount === 0 && readyCount >= remainingItems) {
      status = 'ready';
    } else if (readyCount > 0) {
      status = 'partial';
    }

    return { 
      ready: readyCount, 
      total: remainingItems,
      percentage, 
      status, 
      readyUnits,
      deliveredCount,
      waitingCount,
      totalItems
    };
  };

  // Memoized readiness map - only recalculates when receipts or inventory change
  const readinessMap = React.useMemo(() => {
    const map = {};
    const pendingList = receipts.filter(r => 
      !RECEIPT_TERMINAL_STATUSES.includes(r.delivery_status)
    );
    const availableUnits = inventoryUnits.filter(u => 
      !u.reserved_for_receipt_id && 
      !u.receipt_item_id &&
      !UNIT_TERMINAL_STATUSES.includes(u.status) && 
      u.status !== 'out_for_delivery'
    );
    for (const receipt of pendingList) {
      map[receipt.id] = calculateReadiness(receipt, inventoryUnits, availableUnits, itemStatusMap);
    }
    return map;
  }, [receipts, inventoryUnits, itemStatusMap]);

  // O(1) lookup wrapper
  const getDeliveryReadiness = (receipt) => {
    return readinessMap[receipt.id] || { ready: 0, total: 0, percentage: 0, status: 'empty', readyUnits: [], deliveredCount: 0, waitingCount: 0 };
  };


  // Filter receipts by readiness
  const pendingReceipts = receipts.filter(r => 
    !RECEIPT_TERMINAL_STATUSES.includes(r.delivery_status)
  );

  // Partial delivery routes: Single-stop planned routes that should show in Ready section
  // These are created when user selects specific items from a receipt for delivery
  const partialDeliveryRoutes = routes.filter(route => 
    route.status === 'planned' && 
    route.total_stops === 1 &&
    (route.route_name?.includes('Partial Delivery') || route.route_name?.includes('Delivery -'))
  );

  // Get receipt IDs that already have a route created (to avoid duplicates)
  const receiptsWithRoutes = new Set(
    routes
      .filter(r => r.status === 'planned' || r.status === 'dispatched' || r.status === 'in_progress')
      .flatMap(r => (r.delivery_route_stops || []).map(s => s.receipt_id))
      .filter(Boolean)
  );

  // Ready orders: ALL remaining (non-delivered) items must be ready - NO items waiting
  // FIXED: Exclude receipts that already have a route created
  const readyOrders = pendingReceipts.filter(r => {
    if (receiptsWithRoutes.has(r.id)) return false; // Already has a route
    const readiness = getDeliveryReadiness(r);
    // Only include orders where ALL non-delivered items are ready (waitingCount === 0)
    return readiness.status === 'ready';
  });

  // Toggle order selection
  const toggleOrderSelection = (receiptId) => {
    setSelectedOrders(prev => 
      prev.includes(receiptId)
        ? prev.filter(id => id !== receiptId)
        : [...prev, receiptId]
    );
  };

  // Toggle partial route selection
  const togglePartialRouteSelection = (routeId) => {
    setSelectedPartialRoutes(prev => 
      prev.includes(routeId)
        ? prev.filter(id => id !== routeId)
        : [...prev, routeId]
    );
  };

  // Select all ready orders (including partial delivery routes)
  const selectAllReady = () => {
    const allReadySelected = selectedOrders.length === readyOrders.length && 
                             selectedPartialRoutes.length === partialDeliveryRoutes.length;
    
    if (allReadySelected) {
      setSelectedOrders([]);
      setSelectedPartialRoutes([]);
    } else {
      setSelectedOrders(readyOrders.map(r => r.id));
      setSelectedPartialRoutes(partialDeliveryRoutes.map(r => r.id));
    }
  };

  // Optimize route using AI
  const optimizeRoute = async () => {
    if (selectedOrders.length < 2) {
      setOptimizedOrder(selectedOrders);
      setManualOrder(selectedOrders); // Also update manual order
      return;
    }

    setOptimizingRoute(true);
    
    try {
      // Get auth token
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        console.error('No session for route optimization');
        setOptimizedOrder(selectedOrders);
        setManualOrder(selectedOrders);
        return;
      }

      const ordersToOptimize = selectedOrders.map(id => {
        const receipt = receipts.find(r => r.id === id);
        return {
          id,
          address: receipt?.delivery_address || receipt?.customer_address || 'Unknown',
          customerName: receipt?.customer_name,
          latitude: receipt?.delivery_latitude || receipt?.latitude || null,
          longitude: receipt?.delivery_longitude || receipt?.longitude || null
        };
      });

      const response = await fetch('/api/optimize-route', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({
          stops: ordersToOptimize,
          startAddress: storeLocation.address || 'Store Location',
          startCoordinates: storeLocation.latitude && storeLocation.longitude ? {
            lat: storeLocation.latitude,
            lng: storeLocation.longitude
          } : null
        })
      });

      if (response.ok) {
        const result = await response.json();
        console.log('✅ Route optimized:', result.method, result.message);
        if (result.googleError) {
          console.warn('⚠️ Google Distance Matrix failed:', result.googleError, '(used coordinate fallback)');
        }
        setOptimizedOrder(result.optimizedOrder || selectedOrders);
        setManualOrder(result.optimizedOrder || selectedOrders); // Also update manual order
      } else {
        const errorBody = await response.text();
        console.error(`❌ Route optimization failed (${response.status}):`, errorBody);
        setOptimizedOrder(selectedOrders);
        setManualOrder(selectedOrders);
      }
    } catch (err) {
      console.error('Route optimization error:', err);
      setOptimizedOrder(selectedOrders);
      setManualOrder(selectedOrders);
    } finally {
      setOptimizingRoute(false);
    }
  };

  // NEW: Drag and Drop handlers for manual ordering
  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    dragNodeRef.current = e.currentTarget;
    setTimeout(() => {
      if (dragNodeRef.current) dragNodeRef.current.style.opacity = '0.5';
    }, 0);
  };

  const handleDragEnter = (e, index) => {
    if (index !== draggedIndex) setDragOverIndex(index);
  };

  const handleDragOver = (e) => e.preventDefault();

  const handleDragEnd = () => {
    if (draggedIndex !== null && dragOverIndex !== null && draggedIndex !== dragOverIndex) {
      const newOrder = [...manualOrder];
      const [draggedItem] = newOrder.splice(draggedIndex, 1);
      newOrder.splice(dragOverIndex, 0, draggedItem);
      setManualOrder(newOrder);
    }
    if (dragNodeRef.current) dragNodeRef.current.style.opacity = '1';
    setDraggedIndex(null);
    setDragOverIndex(null);
    dragNodeRef.current = null;
  };

  const moveStopUp = (index) => {
    if (index === 0) return;
    const newOrder = [...manualOrder];
    [newOrder[index - 1], newOrder[index]] = [newOrder[index], newOrder[index - 1]];
    setManualOrder(newOrder);
  };

  const moveStopDown = (index) => {
    if (index === manualOrder.length - 1) return;
    const newOrder = [...manualOrder];
    [newOrder[index], newOrder[index + 1]] = [newOrder[index + 1], newOrder[index]];
    setManualOrder(newOrder);
  };

  // Get the order list based on current mode
  const getCurrentOrderList = () => {
    if (routeOrderMode === 'manual') return manualOrder;
    return optimizedOrder.length > 0 ? optimizedOrder : selectedOrders;
  };

  // NEW: Open edit route modal
  const openEditRouteModal = (route) => {
    const stops = route.delivery_route_stops || [];
    // Sort stops by stop_order or stop_number
    const sortedStops = [...stops].sort((a, b) => (a.stop_order || a.stop_number || 0) - (b.stop_order || b.stop_number || 0));
    setEditingRoute(route);
    setEditingStops(sortedStops);
    setShowEditRouteModal(true);
  };

  // NEW: Drag handlers for editing existing route
  const handleEditDragStart = (e, index) => {
    setDraggedIndex(index);
    dragNodeRef.current = e.currentTarget;
    setTimeout(() => {
      if (dragNodeRef.current) dragNodeRef.current.style.opacity = '0.5';
    }, 0);
  };

  const handleEditDragEnter = (e, index) => {
    if (index !== draggedIndex) setDragOverIndex(index);
  };

  const handleEditDragEnd = () => {
    if (draggedIndex !== null && dragOverIndex !== null && draggedIndex !== dragOverIndex) {
      const newStops = [...editingStops];
      const [draggedItem] = newStops.splice(draggedIndex, 1);
      newStops.splice(dragOverIndex, 0, draggedItem);
      setEditingStops(newStops);
    }
    if (dragNodeRef.current) dragNodeRef.current.style.opacity = '1';
    setDraggedIndex(null);
    setDragOverIndex(null);
    dragNodeRef.current = null;
  };

  const moveEditStopUp = (index) => {
    if (index === 0) return;
    const newStops = [...editingStops];
    [newStops[index - 1], newStops[index]] = [newStops[index], newStops[index - 1]];
    setEditingStops(newStops);
  };

  const moveEditStopDown = (index) => {
    if (index === editingStops.length - 1) return;
    const newStops = [...editingStops];
    [newStops[index], newStops[index + 1]] = [newStops[index + 1], newStops[index]];
    setEditingStops(newStops);
  };

  // NEW: Save the edited route order
  const saveRouteOrder = async () => {
    if (!editingRoute || editingStops.length === 0) return;
    
    setSavingRouteOrder(true);
    
    try {
      // Update each stop with new stop_number
      for (let i = 0; i < editingStops.length; i++) {
        const stop = editingStops[i];
        await supabase
          .from('delivery_route_stops')
          .update({ 
            stop_number: i + 1 
          })
          .eq('id', stop.id);
      }
      
      // Update the route's stop_order array
      const newStopOrder = editingStops.map(s => s.receipt_id);
      await supabase
        .from('delivery_routes')
        .update({ stop_order: newStopOrder })
        .eq('id', editingRoute.id);
      
      setShowEditRouteModal(false);
      setEditingRoute(null);
      setEditingStops([]);
      
      await loadRoutes();
      if (addNotification) {
        addNotification({ title: 'Success', message: 'Route order updated!' });
      }
      
    } catch (err) {
      console.error('Error saving route order:', err);
      if (addNotification) {
        addNotification({ title: 'Error', message: `Failed to save route order: ${err.message}`, isEscalation: true });
      }
    } finally {
      setSavingRouteOrder(false);
    }
  };

  // NEW: Re-optimize an existing route
  const reoptimizeRoute = async () => {
    if (!editingRoute || editingStops.length < 2) return;
    
    setOptimizingRoute(true);
    
    try {
      // Get auth token
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        console.error('No session for route re-optimization');
        return;
      }

      const stopsToOptimize = editingStops.map(stop => {
        const receipt = receipts.find(r => r.id === stop.receipt_id);
        return {
          id: stop.id,
          receiptId: stop.receipt_id,
          address: stop.address || receipt?.delivery_address || receipt?.customer_address || 'Unknown',
          customerName: receipt?.customer_name || stop.customer_name,
          latitude: stop.latitude || receipt?.delivery_latitude || receipt?.latitude || null,
          longitude: stop.longitude || receipt?.delivery_longitude || receipt?.longitude || null
        };
      });

      const response = await fetch('/api/optimize-route', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({
          stops: stopsToOptimize.map(s => ({ id: s.id, address: s.address, customerName: s.customerName, latitude: s.latitude, longitude: s.longitude })),
          startAddress: storeLocation.address || 'Store Location',
          startCoordinates: storeLocation.latitude && storeLocation.longitude ? {
            lat: storeLocation.latitude,
            lng: storeLocation.longitude
          } : null
        })
      });

      if (response.ok) {
        const result = await response.json();
        console.log('✅ Route re-optimized:', result.method, result.message);
        if (result.googleError) {
          console.warn('⚠️ Google Distance Matrix failed:', result.googleError, '(used coordinate fallback)');
        }
        // Reorder editingStops based on optimized order
        const optimizedIds = result.optimizedOrder || [];
        const reorderedStops = optimizedIds.map(id => 
          editingStops.find(s => s.id === id)
        ).filter(Boolean);
        
        // If optimization returned different IDs (receipt_id vs stop id), try matching by receipt_id
        if (reorderedStops.length !== editingStops.length) {
          const reorderedByReceipt = optimizedIds.map(id => 
            editingStops.find(s => s.receipt_id === id)
          ).filter(Boolean);
          if (reorderedByReceipt.length === editingStops.length) {
            setEditingStops(reorderedByReceipt);
          }
        } else {
          setEditingStops(reorderedStops);
        }
      } else {
        const errorBody = await response.text();
        console.error(`❌ Route re-optimization failed (${response.status}):`, errorBody);
      }
    } catch (err) {
      console.error('Route re-optimization error:', err);
    } finally {
      setOptimizingRoute(false);
    }
  };

  // Create delivery route - WITH INVENTORY_UNIT_IDS FIX
  // Now also handles merging partial delivery routes
  const createRoute = async () => {
    if (selectedOrders.length === 0 && selectedPartialRoutes.length === 0) {
      if (addNotification) {
        addNotification({ title: 'No Selection', message: 'No orders selected', isEscalation: true });
      }
      return;
    }

    setCreatingRoute(true);

    try {
      // CRITICAL: Fetch fresh inventory data before creating route
      // This prevents stale data issues where units appear ready but aren't
      console.log('🔄 Fetching fresh inventory data before route creation...');
      let freshInventoryUnits = [];
      try {
        let query = supabase.from('inventory_units').select('*');
        if (storeId) query = query.eq('store_id', storeId);
        const { data } = await query;
        freshInventoryUnits = data || [];
        console.log(`✅ Fetched ${freshInventoryUnits.length} fresh inventory units`);
      } catch (err) {
        console.error('Error fetching fresh inventory:', err);
        freshInventoryUnits = inventoryUnits; // Fall back to state
      }

      const today = new Date().toISOString().split('T')[0];
      
      // Collect all stops: from regular receipts AND from partial routes
      let allStops = [];
      let allUnitIds = [];
      let totalReadyAcrossStops = 0;
      let stopNumber = 1;

      // 1. Get stops from partial delivery routes first
      for (const partialRouteId of selectedPartialRoutes) {
        const partialRoute = routes.find(r => r.id === partialRouteId);
        if (partialRoute && partialRoute.delivery_route_stops) {
          for (const existingStop of partialRoute.delivery_route_stops) {
            const existingUnitIds = existingStop.inventory_unit_ids || [];
            allStops.push({
              receipt_id: existingStop.receipt_id,
              stop_number: stopNumber++,
              status: 'pending',
              inventory_unit_ids: existingUnitIds,
              address: existingStop.address,
              is_partial_delivery: existingStop.is_partial_delivery,
              selected_items: existingStop.selected_items,
              receipt_item_ids: existingStop.receipt_item_ids,
              driver_notes: existingStop.driver_notes
            });
            allUnitIds.push(...existingUnitIds);
            // Count this stop's ready items toward the "nothing ready" guard below.
            // A partial route's stop is already-vetted ready inventory, but its items
            // may be UNITLESS (marked ready without a serialized inventory_unit) — in
            // that case inventory_unit_ids is empty and readiness is carried by
            // receipt_item_ids / selected_items. Count both so a route built only from
            // partial routes (esp. unitless ones) doesn't falsely trip the guard.
            const unitlessCount = Array.isArray(existingStop.receipt_item_ids)
              ? existingStop.receipt_item_ids.length
              : (Array.isArray(existingStop.selected_items) ? existingStop.selected_items.length : 0);
            totalReadyAcrossStops += existingUnitIds.length + unitlessCount;
          }
        }
      }

      // 2. Get stops from regular receipt selections - USE CURRENT ORDER (manual or optimized)
      const orderToUse = getCurrentOrderList();
      
      for (const receiptId of orderToUse) {
        const receipt = receipts.find(r => r.id === receiptId);
        
        // Use fresh inventory data directly instead of stale state
        const items = receipt?.items || [];
        const receiptItemIds = items.map(i => i.id).filter(Boolean);
        
        // Get units for this receipt from FRESH data
        const allUnitsForReceipt = freshInventoryUnits.filter(u => 
          u.reserved_for_receipt_id === receiptId ||
          (u.receipt_item_id && receiptItemIds.includes(u.receipt_item_id))
        );
        
        console.log(`📦 Stop ${stopNumber}: ${receipt?.customer_name}`);
        console.log(`   - Receipt items:`, items.map(i => i.sku));
        console.log(`   - Fresh units for receipt: ${allUnitsForReceipt.length}`, allUnitsForReceipt.map(u => ({ sku: u.sku, status: u.status })));
        
        // Collect ready units from fresh data
        let readyUnits = [];
        
        items.forEach(item => {
          const skuKey = item.sku?.toLowerCase().trim();
          const receiptItemId = item.id;
          
          // Check by receipt_item_id first
          if (receiptItemId) {
            const linkedUnit = allUnitsForReceipt.find(u => 
              u.receipt_item_id && String(u.receipt_item_id) === String(receiptItemId) &&
              ['at_store', 'in_warehouse', 'reserved'].includes(u.status)
            );
            if (linkedUnit) {
              readyUnits.push(linkedUnit);
              return;
            }
          }
          
          // Fallback to SKU matching
          const skuUnit = allUnitsForReceipt.find(u => 
            u.sku?.toLowerCase().trim() === skuKey &&
            ['at_store', 'in_warehouse', 'reserved'].includes(u.status) &&
            !readyUnits.some(r => r.id === u.id) // Not already added
          );
          if (skuUnit) {
            readyUnits.push(skuUnit);
          }
        });
        
        const unitIds = readyUnits.map(u => u.id);
        console.log(`   - Ready units found: ${unitIds.length}`, readyUnits.map(u => u.sku));

        // Some items can be ready WITHOUT a physical unit (unitless / no-SKU items
        // marked ready via item_statuses). Those produce no unit id but are still
        // deliverable. Collect their receipt_item_ids so the stop can carry them —
        // DriverView reads stop.receipt_item_ids to show/deliver unitless items,
        // and the "nothing ready" guard below counts them too.
        const unitlessReadyItems = [];
        items.forEach(item => {
          const receiptItemId = item.id;
          if (!receiptItemId) return;
          // Skip items that already contributed a unit (avoid double counting).
          const hasUnit = readyUnits.some(u =>
            (u.receipt_item_id && String(u.receipt_item_id) === String(receiptItemId)) ||
            (u.sku?.toLowerCase().trim() === item.sku?.toLowerCase().trim())
          );
          if (hasUnit) return;
          const st = itemStatusMap[String(receiptItemId)];
          if (st && ['at_store', 'in_warehouse', 'reserved'].includes(st)) {
            unitlessReadyItems.push(item);
          }
        });
        const unitlessReceiptItemIds = unitlessReadyItems.map(i => i.id);
        const readyByStatusCount = unitlessReadyItems.reduce((sum, i) => sum + (i.quantity || 1), 0);
        const readyHereCount = unitIds.length + readyByStatusCount;

        // FIXED: Determine if this is a partial delivery
        // Partial = we have SOME items ready but not ALL items from the receipt
        const totalItemsInReceipt = items.reduce((sum, item) => sum + (item.quantity || 1), 0);
        const isPartialDelivery = readyHereCount > 0 && readyHereCount < totalItemsInReceipt;

        console.log(`   - Total items in receipt: ${totalItemsInReceipt}, units ready: ${unitIds.length}, ready-by-status: ${readyByStatusCount}`);
        console.log(`   - Is partial delivery: ${isPartialDelivery}`);

        // selected_items: used by the dashboard/driver UI for SKU display when a
        // stop has no inventory units to read SKUs from. Include the unitless
        // items here so they render with a name/SKU instead of a bare count.
        const unitlessSelectedItems = unitlessReadyItems.map(i => ({
          receipt_item_id: i.id,
          sku: i.sku || null,
          item_name: i.item_name || i.name || null,
          quantity: i.quantity || 1
        }));

        const stopRecord = {
          receipt_id: receiptId,
          stop_number: stopNumber++,
          status: 'pending',
          inventory_unit_ids: unitIds,
          is_partial_delivery: isPartialDelivery  // ADDED: Now properly set!
        };
        // Only attach unitless fields when present, so existing all-unit stops
        // are byte-for-byte unchanged.
        if (unitlessReceiptItemIds.length > 0) {
          stopRecord.receipt_item_ids = unitlessReceiptItemIds;
          stopRecord.selected_items = unitlessSelectedItems;
        }
        allStops.push(stopRecord);
        allUnitIds.push(...unitIds);
        // Track ready-by-status so the "nothing ready" guard below doesn't block
        // routes made up of unitless-ready stops.
        totalReadyAcrossStops += readyHereCount;
      }

      // VALIDATION: Check if any items are actually ready (units OR ready-by-status)
      if (totalReadyAcrossStops === 0) {
        throw new Error('No items are ready for delivery! Please add items to inventory or mark them ready first.');
      }

      // AUTO-OPTIMIZE: If 2+ stops, call the route optimizer
      if (allStops.length >= 2) {
        try {
          const { data: { session } } = await supabase.auth.getSession();
          if (session) {
            const stopsForOptimizer = allStops.map(stop => {
              const receipt = receipts.find(r => r.id === stop.receipt_id);
              return {
                id: stop.receipt_id,
                address: stop.address || receipt?.delivery_address || receipt?.customer_address || 'Unknown',
                customerName: receipt?.customer_name || 'Customer',
                latitude: stop.latitude || receipt?.delivery_latitude || receipt?.latitude || null,
                longitude: stop.longitude || receipt?.delivery_longitude || receipt?.longitude || null
              };
            });

            console.log('🗺️ Auto-optimizing route with', stopsForOptimizer.length, 'stops...');
            console.log('🗺️ Store location:', storeLocation.address, storeLocation.latitude, storeLocation.longitude);
            console.log('🗺️ Stops:', stopsForOptimizer.map(s => ({ name: s.customerName, addr: s.address?.substring(0, 40), hasCoords: !!(s.latitude && s.longitude) })));
            const optResponse = await fetch('/api/optimize-route', {
              method: 'POST',
              headers: { 
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${session.access_token}`
              },
              body: JSON.stringify({
                stops: stopsForOptimizer,
                startAddress: storeLocation.address || 'Store Location',
                startCoordinates: storeLocation.latitude && storeLocation.longitude ? {
                  lat: storeLocation.latitude,
                  lng: storeLocation.longitude
                } : null
              })
            });

            if (optResponse.ok) {
              const optResult = await optResponse.json();
              console.log('✅ Route auto-optimized:', optResult.method, optResult.message, optResult.totalDuration || '');
              if (optResult.googleError) {
                console.warn('⚠️ Google Distance Matrix failed:', optResult.googleError, '(used coordinate fallback)');
              }
              
              // Reorder allStops based on optimized order
              const optimizedIds = optResult.optimizedOrder || [];
              if (optimizedIds.length === allStops.length) {
                const reordered = optimizedIds.map((id, idx) => {
                  const stop = allStops.find(s => s.receipt_id === id);
                  if (stop) return { ...stop, stop_number: idx + 1 };
                  return null;
                }).filter(Boolean);
                
                if (reordered.length === allStops.length) {
                  allStops = reordered;
                  console.log('📍 Stops reordered by optimizer');
                }
              }
            } else {
              const errorBody = await optResponse.text();
              console.error(`❌ Route auto-optimization failed (${optResponse.status}):`, errorBody);
            }
          }
        } catch (optErr) {
          console.error('⚠️ Route optimization error (continuing with original order):', optErr);
        }
      }

      // Create the new combined route
      const { data: route, error: routeError } = await supabase
        .from('delivery_routes')
        .insert({
          store_id: storeId || null,
          route_name: routeName || `Delivery Run - ${new Date().toLocaleDateString()}`,
          delivery_date: today,
          stop_order: allStops.map(s => s.receipt_id),
          total_stops: allStops.length,
          status: 'planned'
        })
        .select()
        .single();

      if (routeError) throw routeError;

      // Add route_id to all stops and insert
      const stopsToInsert = allStops.map((stop, idx) => ({
        ...stop,
        route_id: route.id
        // Note: stop_number is already set in allStops
      }));

      console.log('📍 Creating stops:', stopsToInsert.map(s => ({ 
        receipt_id: s.receipt_id, 
        stop_number: s.stop_number,
        inventory_units: s.inventory_unit_ids?.length,
        unitless_items: s.receipt_item_ids?.length || 0,
        is_partial: s.is_partial_delivery
      })));

      const { error: stopsError } = await supabase.from('delivery_route_stops').insert(stopsToInsert);
      
      if (stopsError) throw stopsError;

      // NOTE: Keep inventory units as 'reserved' until route is dispatched
      // The dispatchRoute function will update them to 'out_for_delivery'
      console.log(`📦 Keeping ${allUnitIds.length} units as reserved until dispatch`);
      // No status update here - units stay as 'reserved', 'at_store', or 'in_warehouse'

      // Delete the old partial routes that were merged
      if (selectedPartialRoutes.length > 0) {
        console.log('🗑️ Cleaning up merged partial routes:', selectedPartialRoutes);
        
        // Delete stops first
        for (const partialRouteId of selectedPartialRoutes) {
          await supabase
            .from('delivery_route_stops')
            .delete()
            .eq('route_id', partialRouteId);
        }
        
        // Delete the routes
        await supabase
          .from('delivery_routes')
          .delete()
          .in('id', selectedPartialRoutes);
      }

      setSelectedOrders([]);
      setSelectedPartialRoutes([]);
      setOptimizedOrder([]);
      setManualOrder([]);
      setRouteName('');
      setShowCreateRouteModal(false);
      setActiveSection('routes');
      
      // CRITICAL: Force full refresh to sync all data
      console.log('🔄 Force refreshing all data after route creation...');
      await loadRoutes();
      await loadInventoryUnits();
      if (onRefresh) onRefresh(); // Refresh parent (ReceiptDashboard)
      
      if (addNotification) {
        addNotification({ title: 'Route Created', message: `Route created with ${allStops.length} stops and ${allUnitIds.length} items!` });
      }

    } catch (err) {
      console.error('Error creating route:', err);
      if (addNotification) {
        addNotification({ title: 'Error', message: `Failed to create route: ${err.message}`, isEscalation: true });
      }
    } finally {
      setCreatingRoute(false);
    }
  };


  // Dispatch route to driver
  const dispatchRoute = async () => {
    if (!selectedRoute || !driverInfo.phone) {
      if (addNotification) {
        addNotification({ title: 'Phone Required', message: 'Please enter driver phone number', isEscalation: true });
      }
      return;
    }

    setDispatching(true);
    
    try {
      // Calculate estimated completion time
      // Average ~20 minutes per stop (drive time + delivery time)
      const stopCount = selectedRoute.delivery_route_stops?.length || 1;
      const estimatedMinutes = stopCount * 20;
      const estimatedCompletion = new Date(Date.now() + estimatedMinutes * 60 * 1000).toISOString();
      
      // 1. Update route status to dispatched
      const { error: updateError } = await supabase
        .from('delivery_routes')
        .update({
          driver_name: driverInfo.name,
          driver_phone: driverInfo.phone,
          driver_language: driverInfo.language || 'en',
          status: 'dispatched',
          dispatched_at: new Date().toISOString(),
          estimated_completion: estimatedCompletion
        })
        .eq('id', selectedRoute.id);

      if (updateError) throw updateError;

      // 2. Get all stops for this route to update inventory
      const { data: stops } = await supabase
        .from('delivery_route_stops')
        .select('inventory_unit_ids, receipt_item_ids, receipt_id, is_partial_delivery, selected_items')
        .eq('route_id', selectedRoute.id);

      console.log('📋 Dispatch: Found stops:', stops?.length, stops?.map(s => ({ receipt_id: s.receipt_id, is_partial: s.is_partial_delivery })));

      // 3. Update inventory units to 'out_for_delivery'
      if (stops && stops.length > 0) {
        for (const stop of stops) {
          let unitIds = stop.inventory_unit_ids || [];
          const isPartial = stop.is_partial_delivery || false;
          const receiptId = stop.receipt_id;
          
          console.log(`📦 Processing stop for receipt: ${receiptId}, isPartial: ${isPartial}`);
          
          // FALLBACK: If no inventory_unit_ids stored, find units by receipt_id
          if (unitIds.length === 0 && receiptId) {
            console.log(`⚠️ No inventory_unit_ids for stop, looking up by receipt_id: ${receiptId}`);
            const { data: foundUnits } = await supabase
              .from('inventory_units')
              .select('id')
              .eq('reserved_for_receipt_id', receiptId)
              .in('status', ['reserved', 'at_store', 'in_warehouse']);
            
            unitIds = (foundUnits || []).map(u => u.id);
            console.log(`📦 Found ${unitIds.length} units by receipt lookup`);
          }
          
          if (unitIds.length > 0) {
            const { error: unitUpdateError } = await supabase
              .from('inventory_units')
              .update({
                status: 'out_for_delivery',
                updated_at: new Date().toISOString()
              })
              .in('id', unitIds);
            
            if (unitUpdateError) {
              console.error('❌ Error updating inventory units:', unitUpdateError);
            } else {
              console.log(`📦 Updated ${unitIds.length} units to out_for_delivery for stop`);
            }
          }

          // 4. Update item_statuses and receipt status - ALWAYS for receipts with stops
          if (receiptId) {
            console.log(`📝 Will update receipt ${receiptId} delivery_status...`);
            // LEGACY stops only (inventory era, no receipt_item_ids on the
            // stop): find the lines by SKU. Every stop created since July
            // carries receipt_item_ids and is handled precisely below, so
            // the SKU match never runs for them. It can only move a line
            // that is staged at the store; a line already delivered or
            // picked up by a second line with the same SKU stays put.
            const hasPreciseIds = Array.isArray(stop.receipt_item_ids) && stop.receipt_item_ids.length > 0;
            let skus = [];
            
            if (!hasPreciseIds && unitIds.length > 0) {
              const { data: units } = await supabase
                .from('inventory_units')
                .select('sku')
                .in('id', unitIds);
              skus = [...new Set((units || []).map(u => u.sku).filter(Boolean))];
            } else if (!hasPreciseIds && stop.selected_items && stop.selected_items.length > 0) {
              // Fallback to selected_items SKUs
              skus = [...new Set(stop.selected_items.map(i => i.sku).filter(Boolean))];
            }
            
            for (const sku of skus) {
              await supabase
                .from('item_statuses')
                .update({
                  status: 'out_for_delivery',
                  notes: `Out for delivery - ${new Date().toLocaleDateString()}`,
                  updated_at: new Date().toISOString()
                })
                .eq('receipt_id', receiptId)
                .ilike('sku', sku)
                .in('status', ITEM_STAGED_STATUSES);
            }
            if (skus.length > 0) {
              console.log(`📋 Updated item_statuses to out_for_delivery for receipt ${receiptId}`);
            }

            // Unitless items have no inventory unit and may have no SKU, so the
            // SKU-based update above can miss them. Update those rows precisely
            // by receipt_item_id so they also advance to out_for_delivery.
            const unitlessIds = stop.receipt_item_ids || [];
            if (unitlessIds.length > 0) {
              // Only lines still staged at the store move onto the truck. A
              // line staff marked delivered or picked up in the drawer
              // between planning and dispatch keeps that status.
              const { error: unitlessStatusErr } = await supabase
                .from('item_statuses')
                .update({
                  status: 'out_for_delivery',
                  notes: `Out for delivery - ${new Date().toLocaleDateString()}`,
                  updated_at: new Date().toISOString()
                })
                .eq('receipt_id', receiptId)
                .in('receipt_item_id', unitlessIds)
                .in('status', ITEM_STAGED_STATUSES);
              if (unitlessStatusErr) {
                console.error('❌ Error updating unitless item_statuses:', unitlessStatusErr);
              } else {
                console.log(`📋 Updated ${unitlessIds.length} unitless item_statuses to out_for_delivery for receipt ${receiptId}`);
              }
            }

            // 5. ALWAYS update receipt delivery_status - this is critical for UI consistency
            console.log(`📝 Updating receipt ${receiptId} - isPartial: ${isPartial}`);
            
            if (isPartial) {
              // Partial delivery - check if there are already delivered items
              const { data: receipt } = await supabase
                .from('receipts')
                .select('delivery_status')
                .eq('id', receiptId)
                .single();
              
              console.log(`   Current receipt status: ${receipt?.delivery_status}`);
              
              // Only update if not already delivered
              if (!RECEIPT_TERMINAL_STATUSES.includes(receipt?.delivery_status)) {
                const { error: receiptError } = await supabase
                  .from('receipts')
                  .update({
                    delivery_status: 'partial_out_for_delivery',
                    updated_at: new Date().toISOString()
                  })
                  .eq('id', receiptId);
                
                if (receiptError) {
                  console.error(`❌ Error updating receipt ${receiptId}:`, receiptError);
                } else {
                  console.log(`✅ Updated receipt ${receiptId} to partial_out_for_delivery`);
                }
              } else {
                console.log(`⏭️ Skipping receipt update - already delivered`);
              }
            } else {
              // Full delivery - all items going out
              const { error: receiptError, data: updatedReceipt } = await supabase
                .from('receipts')
                .update({
                  delivery_status: 'out_for_delivery',
                  updated_at: new Date().toISOString()
                })
                .eq('id', receiptId)
                .select('id, delivery_status');  // Return the updated record to verify
              
              if (receiptError) {
                console.error(`❌ Error updating receipt ${receiptId}:`, receiptError);
              } else {
                console.log(`✅ Updated receipt ${receiptId} to out_for_delivery`, updatedReceipt);
              }
            }
          }
        }
      }

      const driverUrl = `https://exitiumlabs.com/driver/${selectedRoute.id}`;
      
      try {
        // The /api/send-driver-route endpoint requires authentication
        // (secure_sms_endpoint). Without a bearer token it returns 401 and the
        // driver SMS never sends. Attach the current session token, matching
        // every other authenticated fetch in this file.
        const { data: { session } } = await supabase.auth.getSession();
        const smsResponse = await fetch('/api/send-driver-route', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(session?.access_token ? { 'Authorization': `Bearer ${session.access_token}` } : {})
          },
          body: JSON.stringify({
            driverPhone: driverInfo.phone,
            driverName: driverInfo.name,
            routeId: selectedRoute.id,
            routeUrl: driverUrl,
            totalStops: selectedRoute.total_stops,
            language: driverInfo.language || 'en'
          })
        });

        const smsResult = await smsResponse.json();
        if (!smsResult.success) {
          console.warn('SMS may not have sent:', smsResult.error);
        }
      } catch (smsErr) {
        console.error('SMS send error (route still dispatched):', smsErr);
      }

      setDispatching(false);
      setShowDispatchModal(false);
      setSelectedRoute(null);
      setDriverInfo({ name: '', phone: '', language: 'en' });
      
      // CRITICAL: Force full refresh to sync all data
      console.log('🔄 Force refreshing all data after dispatch...');
      await loadRoutes();
      await loadInventoryUnits();
      if (onRefresh) onRefresh(); // Refresh parent (ReceiptDashboard)

      if (addNotification) {
        addNotification({ title: 'Route Dispatched', message: `Route dispatched to ${driverInfo.name || 'driver'}! Driver link copied.` });
      }

    } catch (err) {
      console.error('Error dispatching route:', err);
      if (addNotification) {
        addNotification({ title: 'Error', message: `Failed to dispatch route: ${err.message}`, isEscalation: true });
      }
      setDispatching(false);
    }
  };

  // After a dispatched route is deleted, put each receipt back to what its
  // lines say: 'partially_delivered' when anything already reached the
  // customer, else 'pending'. Only receipts the dispatch moved onto a truck
  // are touched, so one closed meanwhile is left alone.
  const resetReceiptsAfterWalkBack = async (receiptIds) => {
    const ids = [...new Set((receiptIds || []).filter(Boolean))];
    if (ids.length === 0) return;
    const { data: terminalRows } = await supabase
      .from('item_statuses')
      .select('receipt_id')
      .in('receipt_id', ids)
      .in('status', ITEM_TERMINAL_STATUSES);
    const hasDelivered = new Set((terminalRows || []).map(r => r.receipt_id));
    const partial = ids.filter(id => hasDelivered.has(id));
    const open = ids.filter(id => !hasDelivered.has(id));
    const now = new Date().toISOString();
    const onTruck = ['out_for_delivery', 'partial_out_for_delivery'];
    if (partial.length > 0) {
      await supabase
        .from('receipts')
        .update({ delivery_status: 'partially_delivered', updated_at: now })
        .in('id', partial)
        .in('delivery_status', onTruck);
    }
    if (open.length > 0) {
      await supabase
        .from('receipts')
        .update({ delivery_status: 'pending', updated_at: now })
        .in('id', open)
        .in('delivery_status', onTruck);
    }
    console.log(`🔄 Walked back ${ids.length} receipt(s): ${partial.length} partially_delivered, ${open.length} pending`);
  };

  // Delete a route
  const deleteRoute = async (routeId) => {
    setDeletingRouteId(routeId);
    
    try {
      // Get the route stops to reset inventory units and receipts.
      // Also pull receipt_item_ids: UNITLESS items (marked ready without a
      // serialized inventory unit) carry their state in item_statuses, not in
      // inventory_units. Deleting a dispatched route must walk those back too,
      // otherwise they stay stuck at out_for_delivery and calculateReadiness
      // treats them as "on the truck" — never returning to Ready.
      const { data: stops } = await supabase
        .from('delivery_route_stops')
        .select('inventory_unit_ids, receipt_id, receipt_item_ids')
        .eq('route_id', routeId);
      
      // Reset inventory units back to reserved (only if they were out_for_delivery)
      const allUnitIds = (stops || []).flatMap(s => s.inventory_unit_ids || []);
      if (allUnitIds.length > 0) {
        await supabase
          .from('inventory_units')
          .update({ status: 'reserved' })
          .in('id', allUnitIds)
          .eq('status', 'out_for_delivery');
      }

      // Reset UNITLESS item_statuses back to at_store (the "ready" state the
      // partial-delivery flow originally set). Mirror of the dispatch path,
      // which advances these same receipt_item_ids to out_for_delivery.
      for (const stop of (stops || [])) {
        const unitlessIds = stop.receipt_item_ids || [];
        if (unitlessIds.length === 0 || !stop.receipt_id) continue;
        const { error: unitlessResetErr } = await supabase
          .from('item_statuses')
          .update({
            status: 'at_store',
            notes: `Returned to ready - ${new Date().toLocaleDateString()}`,
            updated_at: new Date().toISOString()
          })
          .eq('receipt_id', stop.receipt_id)
          .in('receipt_item_id', unitlessIds)
          .eq('status', 'out_for_delivery');
        if (unitlessResetErr) {
          console.error('❌ Error resetting unitless item_statuses:', unitlessResetErr);
        } else {
          console.log(`🔄 Reset ${unitlessIds.length} unitless item_statuses back to at_store for receipt ${stop.receipt_id}`);
        }
      }

      // Walk the receipts back off the truck: 'partially_delivered' if any
      // line is already with the customer, else 'pending'.
      await resetReceiptsAfterWalkBack((stops || []).map(s => s.receipt_id));

      // Delete route stops
      const { error: stopsError } = await supabase
        .from('delivery_route_stops')
        .delete()
        .eq('route_id', routeId);

      if (stopsError) {
        console.error('Error deleting route stops:', stopsError);
      }

      // Delete the route
      const { error: routeError } = await supabase
        .from('delivery_routes')
        .delete()
        .eq('id', routeId);

      if (routeError) throw routeError;

      setConfirmDeleteId(null);
      loadRoutes();

    } catch (err) {
      console.error('Error deleting route:', err);
      if (addNotification) {
        addNotification({ title: 'Error', message: `Failed to delete route: ${err.message}`, isEscalation: true });
      }
    } finally {
      setDeletingRouteId(null);
    }
  };

  // Bulk delete multiple routes
  const bulkDeleteRoutes = async (routeIds) => {
    if (!routeIds || routeIds.length === 0) return;
    
    setCleaningUp(true);
    
    try {
      for (const routeId of routeIds) {
        // Get stops for this route (include receipt_item_ids for unitless reset)
        const { data: stops } = await supabase
          .from('delivery_route_stops')
          .select('inventory_unit_ids, receipt_id, receipt_item_ids')
          .eq('route_id', routeId);
        
        // Reset inventory units (only if they were out_for_delivery)
        const unitIds = (stops || []).flatMap(s => s.inventory_unit_ids || []);
        if (unitIds.length > 0) {
          await supabase
            .from('inventory_units')
            .update({ status: 'reserved' })
            .in('id', unitIds)
            .eq('status', 'out_for_delivery');
        }

        // Reset UNITLESS item_statuses back to at_store (mirror of dispatch).
        for (const stop of (stops || [])) {
          const unitlessIds = stop.receipt_item_ids || [];
          if (unitlessIds.length === 0 || !stop.receipt_id) continue;
          await supabase
            .from('item_statuses')
            .update({
              status: 'at_store',
              notes: `Returned to ready - ${new Date().toLocaleDateString()}`,
              updated_at: new Date().toISOString()
            })
            .eq('receipt_id', stop.receipt_id)
            .in('receipt_item_id', unitlessIds)
            .eq('status', 'out_for_delivery');
        }

        // Walk the receipts back off the truck (see resetReceiptsAfterWalkBack).
        await resetReceiptsAfterWalkBack((stops || []).map(s => s.receipt_id));

        // Delete stops
        await supabase
          .from('delivery_route_stops')
          .delete()
          .eq('route_id', routeId);

        // Delete route
        await supabase
          .from('delivery_routes')
          .delete()
          .eq('id', routeId);
      }

      setSelectedRoutesForDelete([]);
      setShowCleanupModal(false);
      loadRoutes();
      if (addNotification) {
        addNotification({ title: 'Routes Deleted', message: `Deleted ${routeIds.length} route(s)` });
      }

    } catch (err) {
      console.error('Error bulk deleting routes:', err);
      if (addNotification) {
        addNotification({ title: 'Error', message: `Failed to delete routes: ${err.message}`, isEscalation: true });
      }
    } finally {
      setCleaningUp(false);
    }
  };

  // Delete all non-completed routes (cleanup)
  const cleanupOldRoutes = async (type = 'all') => {
    setCleaningUp(true);
    
    try {
      let query = supabase.from('delivery_routes').select('id');
      
      if (storeId) {
        query = query.eq('store_id', storeId);
      }
      
      if (type === 'planned') {
        query = query.eq('status', 'planned');
      } else if (type === 'incomplete') {
        query = query.in('status', ['planned', 'dispatched']);
      } else if (type === 'completed') {
        query = query.eq('status', 'completed');
      }
      // 'all' = no filter
      
      const { data: routesToDelete } = await query;
      
      if (!routesToDelete || routesToDelete.length === 0) {
        if (addNotification) {
          addNotification({ title: 'Info', message: 'No routes to delete' });
        }
        setCleaningUp(false);
        return;
      }
      
      const routeIds = routesToDelete.map(r => r.id);
      await bulkDeleteRoutes(routeIds);
      
    } catch (err) {
      console.error('Error cleaning up routes:', err);
      if (addNotification) {
        addNotification({ title: 'Error', message: `Failed to cleanup routes: ${err.message}`, isEscalation: true });
      }
      setCleaningUp(false);
    }
  };

  // Filter routes by status
  const filteredRoutes = routes.filter(route => {
    // Exclude partial delivery routes (they show in Ready section)
    const isPartialDelivery = route.status === 'planned' && 
                              route.total_stops === 1 &&
                              (route.route_name?.includes('Partial Delivery') || route.route_name?.includes('Delivery -'));
    if (isPartialDelivery) return false;
    
    if (routeFilter === 'active') {
      return ['planned', 'dispatched', 'in_progress'].includes(route.status);
    } else if (routeFilter === 'completed') {
      return route.status === 'completed';
    }
    return true; // 'all'
  });

  // Sort routes: active first, then by date
  const sortedRoutes = [...filteredRoutes].sort((a, b) => {
    const statusOrder = { 'in_progress': 0, 'dispatched': 1, 'planned': 2, 'completed': 3 };
    const statusDiff = (statusOrder[a.status] || 99) - (statusOrder[b.status] || 99);
    if (statusDiff !== 0) return statusDiff;
    return new Date(b.created_at) - new Date(a.created_at);
  });

  // Generate Google Maps URL for route
  const generateMapsUrl = (routeStops) => {
    if (!routeStops || routeStops.length === 0) return '';

    const addresses = routeStops.map(stop => {
      const receipt = receipts.find(r => r.id === stop.receipt_id);
      return encodeURIComponent(receipt?.delivery_address || '');
    }).filter(Boolean);

    if (addresses.length === 0) return '';

    const destination = addresses[addresses.length - 1];
    const waypoints = addresses.slice(0, -1).join('|');
    
    return `https://www.google.com/maps/dir/?api=1&destination=${destination}&waypoints=${waypoints}&travelmode=driving`;
  };

  // Format phone for display
  const formatPhone = (phone) => {
    if (!phone) return '';
    const digits = phone.replace(/\D/g, '');
    if (digits.length === 10) {
      return `(${digits.slice(0,3)}) ${digits.slice(3,6)}-${digits.slice(6)}`;
    }
    return phone;
  };

  // Order Card Component — premium redesign
  const OrderCard = ({ receipt, selectable = false, showDetails = true }) => {
    const readiness = getDeliveryReadiness(receipt);
    const isSelected = selectedOrders.includes(receipt.id);
    const isReady = readiness.status === 'ready';
    const hasAddress = Boolean(receipt.delivery_address);
    const initial = (receipt.customer_name || '?').trim().charAt(0).toUpperCase();
    // Items still owed to the customer (excludes already-delivered)
    const remainingItems = readiness.total ?? Math.max((readiness.totalItems || 0) - (readiness.deliveredCount || 0), 0);
    const itemsToDeliver = remainingItems || receipt.items?.length || 0;

    return (
      <div
        onClick={() => selectable && toggleOrderSelection(receipt.id)}
        className={`group relative rounded-2xl border bg-white transition-all duration-200 overflow-hidden ${
          selectable ? 'cursor-pointer' : ''
        } ${
          isSelected
            ? 'border-blue-500 ring-2 ring-blue-500/20 shadow-[0_8px_24px_-8px_rgba(37,99,235,0.30)]'
            : 'border-slate-200 shadow-[0_1px_2px_rgba(15,23,42,0.04),0_1px_3px_rgba(15,23,42,0.06)] hover:border-slate-300 hover:shadow-[0_8px_20px_-6px_rgba(15,23,42,0.12)] hover:-translate-y-px'
        }`}
      >
        {/* Status accent bar */}
        <div
          className={`absolute left-0 top-0 bottom-0 w-1 ${
            isSelected ? 'bg-blue-500' : isReady ? 'bg-emerald-400' : 'bg-amber-400'
          }`}
        />

        <div className="p-4 pl-5">
          <div className="flex items-start gap-3">
            {/* Round selection control */}
            {selectable && (
              <div
                className={`mt-2 flex-shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all ${
                  isSelected
                    ? 'bg-blue-600 border-blue-600'
                    : 'bg-white border-slate-300 group-hover:border-blue-400'
                }`}
              >
                {isSelected && <Check className="w-3 h-3 text-white" strokeWidth={3} />}
              </div>
            )}

            {/* Customer avatar */}
            <div
              className={`flex-shrink-0 w-9 h-9 rounded-xl border flex items-center justify-center text-sm font-bold ${
                isReady
                  ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
                  : 'bg-amber-50 border-amber-200 text-amber-700'
              }`}
            >
              {initial}
            </div>

            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-2">
                <h4 className="font-semibold text-slate-900 truncate flex-1 min-w-0">
                  {receipt.customer_name}
                </h4>
                {isReady ? (
                  <span className="flex-shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200 whitespace-nowrap">
                    <CheckCircle className="w-3 h-3" />
                    Ready to deliver
                  </span>
                ) : (
                  <span className="flex-shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-amber-50 text-amber-700 border border-amber-200 whitespace-nowrap">
                    <Clock className="w-3 h-3" />
                    {readiness.waitingCount} item{readiness.waitingCount !== 1 ? 's' : ''} on order
                  </span>
                )}
              </div>

              {showDetails && (
                <>
                  <div className="flex items-center gap-1.5 text-sm mt-1">
                    <MapPin className={`w-3.5 h-3.5 flex-shrink-0 ${hasAddress ? 'text-slate-400' : 'text-amber-500'}`} />
                    {hasAddress ? (
                      <span className="truncate text-slate-600">{receipt.delivery_address}</span>
                    ) : (
                      <span className="truncate text-amber-600 font-medium">No address yet — add one before routing</span>
                    )}
                  </div>

                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500 mt-1.5">
                    <span className="flex items-center gap-1">
                      <Package className="w-3 h-3" />
                      {itemsToDeliver} item{itemsToDeliver !== 1 ? 's' : ''} to deliver
                    </span>
                    {receipt.customer_phone && (
                      <span className="flex items-center gap-1">
                        <Phone className="w-3 h-3" />
                        {formatPhone(receipt.customer_phone)}
                      </span>
                    )}
                    {(receipt.internal_notes || receipt.customer_notes) && (
                      <span className="flex items-center gap-1 text-amber-600 font-medium">
                        <Info className="w-3 h-3" />
                        Special instructions
                      </span>
                    )}
                  </div>

                  {/* Inventory progress — only shown while items are still arriving */}
                  {!isReady && (
                    <div className="mt-3 pt-3 border-t border-slate-100">
                      <div className="flex items-center justify-between text-[11px] mb-1.5">
                        <span className="text-slate-500">
                          <span className="font-semibold text-slate-700">{readiness.ready}</span> of {remainingItems} item{remainingItems !== 1 ? 's' : ''} in stock
                          {readiness.deliveredCount > 0 && (
                            <span className="text-slate-400"> · {readiness.deliveredCount} already delivered</span>
                          )}
                        </span>
                        <span className="font-semibold text-slate-700">{readiness.percentage}%</span>
                      </div>
                      <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-emerald-400 to-emerald-500 rounded-full transition-all duration-500"
                          style={{ width: `${readiness.percentage}%` }}
                        />
                      </div>
                      <p className="mt-1.5 text-[11px] text-slate-400">
                        Moves to Ready automatically once the remaining item{readiness.waitingCount !== 1 ? 's arrive' : ' arrives'}
                      </p>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Route Card Component - Redesigned
  const RouteCard = ({ route }) => {
    const statusConfig = {
      planned: { 
        bg: 'bg-blue-500/10', 
        border: 'border-blue-500/20',
        text: 'text-blue-400', 
        label: 'Planned',
        dot: 'bg-blue-400'
      },
      dispatched: { 
        bg: 'bg-blue-50', 
        border: 'border-blue-200',
        text: 'text-blue-600', 
        label: 'Dispatched',
        dot: 'bg-blue-400'
      },
      in_progress: { 
        bg: 'bg-amber-50', 
        border: 'border-amber-500/20',
        text: 'text-amber-600', 
        label: 'In Progress',
        dot: 'bg-amber-400 animate-pulse'
      },
      completed: { 
        bg: 'bg-emerald-50', 
        border: 'border-emerald-500/20',
        text: 'text-emerald-600', 
        label: 'Completed',
        dot: 'bg-emerald-400'
      },
      cancelled: { 
        bg: 'bg-gray-500/10', 
        border: 'border-gray-500/20',
        text: 'text-slate-600', 
        label: 'Cancelled',
        dot: 'bg-gray-400'
      }
    };

    const status = statusConfig[route.status] || statusConfig.planned;
    const completedStops = route.delivery_route_stops?.filter(s => s.status === 'delivered').length || 0;
    const totalStops = route.total_stops || route.delivery_route_stops?.length || 0;
    const progressPercent = totalStops > 0 ? (completedStops / totalStops) * 100 : 0;
    const isDeleting = deletingRouteId === route.id;
    const showDeleteConfirm = confirmDeleteId === route.id;

    return (
      <div className={`group relative rounded-2xl border transition-all duration-300 overflow-hidden shadow-[0_1px_2px_rgba(15,23,42,0.04),0_1px_3px_rgba(15,23,42,0.06)] ${
        showDeleteConfirm 
          ? 'bg-red-500/5 border-red-300' 
          : 'bg-white border-slate-300 hover:border-slate-400 hover:shadow-[0_4px_12px_-2px_rgba(15,23,42,0.10)] hover:-translate-y-px'
      }`}>
        {/* Main Content */}
        <div className="p-4 sm:p-5">
          {/* Top Row: Name, Status, Actions */}
          <div className="flex items-start justify-between gap-4 mb-4">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3 mb-1">
                <h3 className="text-slate-900 font-semibold text-base truncate">
                  {route.route_name || 'Untitled Route'}
                </h3>
                <div className={`flex items-center gap-1.5 px-2 py-0.5 rounded-full ${status.bg} ${status.border} border`}>
                  <div className={`w-1.5 h-1.5 rounded-full ${status.dot}`} />
                  <span className={`text-xs font-medium ${status.text}`}>{status.label}</span>
                </div>
              </div>
              <div className="flex items-center gap-3 text-sm text-slate-500">
                <span className="flex items-center gap-1.5">
                  <Calendar className="w-3.5 h-3.5" />
                  {new Date(route.delivery_date).toLocaleDateString('en-US', { 
                    weekday: 'short', 
                    month: 'short', 
                    day: 'numeric' 
                  })}
                </span>
                {route.driver_name && (
                  <span className="flex items-center gap-1.5">
                    <User className="w-3.5 h-3.5" />
                    {route.driver_name}
                  </span>
                )}
              </div>
            </div>

            {/* Delete Button */}
            {!showDeleteConfirm && (
              <button
                onClick={() => setConfirmDeleteId(route.id)}
                className="opacity-0 group-hover:opacity-100 p-2 text-slate-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all"
                title="Delete route"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            )}
          </div>

          {/* Progress Section */}
          <div className="mb-4">
            <div className="flex items-center justify-between text-xs mb-2">
              <span className="text-slate-600">Delivery Progress</span>
              <span className="text-slate-900 font-medium">{completedStops} of {totalStops} stops</span>
            </div>
            <div className="h-1.5 bg-slate-50 rounded-full overflow-hidden">
              <div 
                className={`h-full rounded-full transition-all duration-500 ${
                  progressPercent === 100 
                    ? 'bg-emerald-500' 
                    : 'bg-blue-600'
                }`}
                style={{ width: `${progressPercent}%` }}
              />
            </div>
          </div>

          {/* Delete Confirmation */}
          {showDeleteConfirm ? (
            <div className="flex items-center justify-between p-3 bg-red-50 border border-red-200 rounded-xl">
              <span className="text-sm text-red-600">Delete this route?</span>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setConfirmDeleteId(null)}
                  className="px-3 py-1.5 text-xs font-medium text-slate-600 hover:text-slate-900 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => deleteRoute(route.id)}
                  disabled={isDeleting}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-red-500 hover:bg-red-600 rounded-lg text-slate-900 text-xs font-medium transition-all disabled:opacity-50 active:scale-[0.98]"
                >
                  {isDeleting ? (
                    <Loader2 className="w-3 h-3 animate-spin" />
                  ) : (
                    <Trash2 className="w-3 h-3" />
                  )}
                  Delete
                </button>
              </div>
            </div>
          ) : (
            /* Action Buttons - Responsive: wrap on mobile */
            <div className="flex flex-wrap items-center gap-2">
              {route.status === 'planned' && (
                <button
                  onClick={() => {
                    setSelectedRoute(route);
                    setShowDispatchModal(true);
                  }}
                  className="flex-1 min-w-[100px] flex items-center justify-center gap-1.5 py-2.5 px-3 bg-blue-600 rounded-xl text-white text-sm font-semibold hover:shadow-lg hover:shadow-blue-500/10 transition-all active:scale-[0.98]"
                >
                  <Send className="w-4 h-4 flex-shrink-0" />
                  Dispatch
                </button>
              )}

              {/* NEW: Edit Order button for planned routes */}
              {route.status === 'planned' && route.delivery_route_stops?.length > 1 && (
                <button
                  onClick={() => openEditRouteModal(route)}
                  className="flex items-center justify-center gap-1.5 py-2.5 px-3 bg-blue-100 hover:bg-blue-200 border border-blue-300 rounded-xl text-blue-600 hover:text-blue-600 text-sm font-medium transition-all"
                >
                  <ArrowUpDown className="w-4 h-4 flex-shrink-0" />
                  Edit Order
                </button>
              )}
              
              {route.status !== 'planned' && (
                <div className="flex-1 min-w-[100px] flex items-center gap-2 py-2 px-3 bg-slate-50 rounded-xl">
                  {route.status === 'completed' ? (
                    <Check className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                  ) : (
                    <Loader2 className="w-4 h-4 text-blue-600 animate-spin flex-shrink-0" />
                  )}
                  <span className={`text-sm font-medium truncate ${
                    route.status === 'completed' ? 'text-emerald-600' : 'text-blue-600'
                  }`}>
                    {route.status === 'completed' ? 'Delivered' : 'En Route'}
                  </span>
                </div>
              )}
              
              <a
                href={`/driver/${route.id}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-1.5 py-2.5 px-3 bg-slate-50 hover:bg-slate-100 border border-slate-200 rounded-xl text-slate-600 hover:text-slate-900 text-sm font-medium transition-all"
              >
                <Eye className="w-4 h-4 flex-shrink-0" />
                View
              </a>

              {route.status !== 'completed' && route.delivery_route_stops?.length > 0 && (
                <a
                  href={generateMapsUrl(route.delivery_route_stops)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-1.5 py-2.5 px-3 bg-slate-50 hover:bg-slate-100 border border-slate-200 rounded-xl text-slate-600 hover:text-slate-900 text-sm font-medium transition-all"
                >
                  <Navigation className="w-4 h-4 flex-shrink-0" />
                  Maps
                </a>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className={hideHeader ? "h-full flex flex-col" : "h-full flex flex-col overflow-x-hidden max-h-[calc(100vh-6rem)] rounded-2xl border border-slate-200/80 shadow-[0_1px_3px_rgba(15,23,42,0.04),0_8px_24px_-12px_rgba(15,23,42,0.12)] bg-white overflow-hidden"}>
      {/* Header - Compact for both Mobile & Desktop - STICKY on desktop */}
      {!hideHeader && (
        <div className="flex-shrink-0 bg-gradient-to-b from-white to-slate-50/80 backdrop-blur-xl p-4 border-b border-slate-200 sticky top-0 z-10 shadow-[0_1px_2px_rgba(15,23,42,0.03)]">
          {/* Title & Actions Row */}
          <div className="flex items-center justify-between mb-3">
            <div>
              <h2 className="text-xl font-bold text-slate-900">
                Delivery <span className="text-blue-600">Routes</span>
              </h2>
              <p className="text-slate-500 text-xs mt-0.5">
                Plan routes and dispatch drivers
              </p>
            </div>

            {/* Action Buttons - Compact */}
            <div className="flex gap-2">
              <button
                onClick={() => setShowDriverMap(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 border border-blue-600 rounded-lg text-white font-semibold text-sm hover:bg-blue-700 transition-all"
              >
                <Radio className="w-4 h-4 text-white" />
                <span>Track Drivers</span>
              </button>

              <button 
                onClick={() => onOpenSettings && onOpenSettings()}
                className={`flex items-center gap-1.5 px-3 py-1.5 border rounded-lg text-sm font-medium transition-all group ${
                  storeLocation.address 
                    ? 'bg-emerald-50 border-emerald-300 text-emerald-600 hover:bg-emerald-100' 
                    : 'bg-amber-50 border-amber-300 text-amber-600 hover:bg-amber-100'
                }`}
                title={storeLocation.address || 'Set store location in Settings'}
              >
                <MapPin className="w-4 h-4" />
                <span className="max-w-[120px] truncate">
                  {storeLocation.address 
                    ? storeLocation.address.split(',')[0] 
                    : 'Set Location'}
                </span>
                {storeLocation.latitude && storeLocation.longitude && (
                  <Check className="w-3 h-3" />
                )}
                <ExternalLink className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
              </button>
            </div>
          </div>

          {/* Section Tabs — segmented control */}
          <div className="inline-flex items-center gap-1 p-1 bg-slate-100 rounded-xl">
            {[
              { key: 'ready', label: 'Ready', count: readyOrders.length + partialDeliveryRoutes.length, dot: 'bg-emerald-500' },
              { key: 'routes', label: 'Routes', count: filteredRoutes.length, dot: 'bg-blue-500' },
            ].map(tab => (
              <button
                key={tab.key}
                onClick={() => setActiveSection(tab.key)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  activeSection === tab.key
                    ? 'bg-white text-slate-900 shadow-[0_1px_2px_rgba(15,23,42,0.08),0_2px_6px_-2px_rgba(15,23,42,0.10)]'
                    : 'text-slate-500 hover:text-slate-800'
                }`}
              >
                <span className={`w-1.5 h-1.5 rounded-full ${tab.dot} ${activeSection === tab.key ? '' : 'opacity-40'}`} />
                {tab.label}
                <span className={`min-w-[20px] px-1.5 py-0.5 rounded-md text-xs font-semibold text-center ${
                  activeSection === tab.key ? 'bg-slate-100 text-slate-700' : 'bg-slate-200/70 text-slate-500'
                }`}>
                  {tab.count}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Compact toolbar when hideHeader=true (mobile) - title is in sticky header, just buttons & pills here - STICKY */}
      {hideHeader && (
        <div className="sticky top-0 z-20 bg-white px-4 pt-2 pb-3 space-y-3 border-b border-slate-200" style={{ position: '-webkit-sticky' }}>
          {/* Action Buttons Row */}
          <div className="flex gap-2">
            <button
              onClick={() => setShowDriverMap(true)}
              className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-blue-100 border border-blue-300 rounded-xl text-blue-600 font-medium text-sm"
            >
              <Radio className="w-4 h-4" />
              <span>Track Drivers</span>
            </button>
            <button 
              onClick={() => onOpenSettings && onOpenSettings()}
              className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 border rounded-xl text-sm font-medium ${
                storeLocation.address 
                  ? 'bg-emerald-50 border-emerald-300 text-emerald-600' 
                  : 'bg-amber-50 border-amber-300 text-amber-600'
              }`}
            >
              <MapPin className="w-4 h-4" />
              <span className="truncate max-w-[100px]">
                {storeLocation.address 
                  ? storeLocation.address.split(',')[0] 
                  : 'Set Location'}
              </span>
              {storeLocation.latitude && storeLocation.longitude && (
                <Check className="w-3 h-3" />
              )}
            </button>
          </div>

          {/* Filter Pills Row */}
          <div className="flex gap-1 p-1 bg-slate-100 rounded-xl overflow-x-auto no-scrollbar">
            {[
              { key: 'ready', label: 'Ready', count: readyOrders.length + partialDeliveryRoutes.length, dot: 'bg-emerald-500' },
              { key: 'routes', label: 'Routes', count: filteredRoutes.length, dot: 'bg-blue-500' },
            ].map(tab => (
              <button
                key={tab.key}
                onClick={() => setActiveSection(tab.key)}
                className={`flex-1 flex-shrink-0 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold transition-all whitespace-nowrap ${
                  activeSection === tab.key
                    ? 'bg-white text-slate-900 shadow-[0_1px_2px_rgba(15,23,42,0.08),0_2px_6px_-2px_rgba(15,23,42,0.10)]'
                    : 'text-slate-500'
                }`}
              >
                <span className={`w-1.5 h-1.5 rounded-full ${tab.dot} ${activeSection === tab.key ? '' : 'opacity-40'}`} />
                {tab.label} ({tab.count})
              </button>
            ))}
          </div>

          {/* Routes Sub-Filter (Active/Completed/All) - Only show when on Routes section */}
          {activeSection === 'routes' && (
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-1 p-1 bg-slate-50 rounded-xl">
                <button
                  onClick={() => setRouteFilter('active')}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                    routeFilter === 'active'
                      ? 'bg-blue-100 text-blue-600 border border-blue-300'
                      : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
                  }`}
                >
                  Active ({routes.filter(r => ['planned', 'dispatched', 'in_progress'].includes(r.status)).length})
                </button>
                <button
                  onClick={() => setRouteFilter('completed')}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                    routeFilter === 'completed'
                      ? 'bg-emerald-100 text-emerald-600 border border-emerald-300'
                      : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
                  }`}
                >
                  Completed ({routes.filter(r => r.status === 'completed').length})
                </button>
                <button
                  onClick={() => setRouteFilter('all')}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                    routeFilter === 'all'
                      ? 'bg-blue-100 text-blue-600 border border-blue-300'
                      : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
                  }`}
                >
                  All ({routes.length})
                </button>
              </div>
              
              {/* Cleanup Button */}
              <button
                onClick={() => setShowCleanupModal(true)}
                className="flex-shrink-0 flex items-center gap-1.5 px-2.5 py-1.5 bg-red-50 border border-red-200 rounded-lg text-red-600 text-xs font-medium hover:bg-red-100 transition-all"
              >
                <Trash2 className="w-3 h-3" />
                <span className="hidden sm:inline">Cleanup</span>
              </button>
            </div>
          )}
        </div>
      )}

      {/* Content */}
      <div className={`flex-1 ${hideHeader ? '' : 'overflow-y-auto overflow-x-hidden bg-slate-100/70 shadow-[inset_0_2px_4px_-2px_rgba(15,23,42,0.06)]'} p-4 ${(selectedOrders.length > 0 || selectedPartialRoutes.length > 0) ? 'pb-44 md:pb-36' : ''}`}>
        {/* Ready Orders Section */}
        {activeSection === 'ready' && (
          <div className="space-y-4">
            {(readyOrders.length > 0 || partialDeliveryRoutes.length > 0) && (
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-4">
                <div>
                  <h3 className="text-sm font-semibold text-slate-900">
                    {readyOrders.length + partialDeliveryRoutes.length} order{(readyOrders.length + partialDeliveryRoutes.length) !== 1 ? 's' : ''} ready for delivery
                  </h3>
                  <p className="text-xs text-slate-500 mt-0.5">
                    Select the stops you want to deliver, then create a route
                  </p>
                </div>
                <button
                  onClick={selectAllReady}
                  className="self-start sm:self-auto flex items-center gap-2 px-3 py-1.5 rounded-lg border text-sm font-medium transition-all bg-white border-slate-200 text-blue-600 hover:border-blue-300 hover:bg-blue-50"
                >
                  {selectedOrders.length === readyOrders.length && selectedPartialRoutes.length === partialDeliveryRoutes.length ? (
                    <>
                      <CheckSquare className="w-4 h-4" />
                      Clear selection
                    </>
                  ) : (
                    <>
                      <Square className="w-4 h-4" />
                      Select all
                    </>
                  )}
                </button>
              </div>
            )}

            {readyOrders.length === 0 && partialDeliveryRoutes.length === 0 ? (
              <div className="text-center py-16">
                <div className="w-16 h-16 rounded-2xl bg-white border border-slate-200 shadow-[0_1px_3px_rgba(15,23,42,0.06)] flex items-center justify-center mx-auto mb-4">
                  <Package className="w-8 h-8 text-slate-400" />
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">No orders ready yet</h3>
                <p className="text-slate-500 text-sm max-w-xs mx-auto">
                  Orders appear here once every item is in stock and ready to load
                </p>
              </div>
            ) : (
              <div className="grid gap-3 pb-36">
                {/* Partial Delivery Routes (from receipt item selections) */}
                {partialDeliveryRoutes.map(route => {
                  const isSelected = selectedPartialRoutes.includes(route.id);
                  const stop = route.delivery_route_stops?.[0];
                  const receipt = receipts.find(r => r.id === stop?.receipt_id);
                  const itemCount = stop?.inventory_unit_ids?.length || stop?.selected_items?.length || 1;
                  
                  return (
                    <div
                      key={`partial-${route.id}`}
                      onClick={() => togglePartialRouteSelection(route.id)}
                      className={`group relative rounded-2xl border bg-white transition-all duration-200 overflow-hidden cursor-pointer ${
                        isSelected
                          ? 'border-blue-500 ring-2 ring-blue-500/20 shadow-[0_8px_24px_-8px_rgba(37,99,235,0.30)]'
                          : 'border-slate-200 shadow-[0_1px_2px_rgba(15,23,42,0.04),0_1px_3px_rgba(15,23,42,0.06)] hover:border-slate-300 hover:shadow-[0_8px_20px_-6px_rgba(15,23,42,0.12)] hover:-translate-y-px'
                      }`}
                    >
                      {/* Status accent bar */}
                      <div className={`absolute left-0 top-0 bottom-0 w-1 ${isSelected ? 'bg-blue-500' : 'bg-blue-400'}`} />

                      {/* Delete control (top-right) */}
                      <div className="absolute top-2 right-2 z-10" onClick={(e) => e.stopPropagation()}>
                        {confirmDeleteId === route.id ? (
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => deleteRoute(route.id)}
                              disabled={deletingRouteId === route.id}
                              className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-semibold bg-red-600 text-white hover:bg-red-700 disabled:opacity-60"
                            >
                              {deletingRouteId === route.id ? (
                                <Loader2 className="w-3 h-3 animate-spin" />
                              ) : (
                                <Trash2 className="w-3 h-3" />
                              )}
                              Delete
                            </button>
                            <button
                              onClick={() => setConfirmDeleteId(null)}
                              disabled={deletingRouteId === route.id}
                              className="px-2 py-1 rounded-lg text-xs font-semibold bg-slate-100 text-slate-600 hover:bg-slate-200 disabled:opacity-60"
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => setConfirmDeleteId(route.id)}
                            title="Delete this partial delivery"
                            className="opacity-0 group-hover:opacity-100 transition-opacity inline-flex items-center justify-center w-7 h-7 rounded-lg bg-white border border-slate-200 text-slate-400 hover:text-red-600 hover:border-red-200 hover:bg-red-50"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>

                      <div className="p-4 pl-5">
                        <div className="flex items-start gap-3">
                          {/* Round selection control */}
                          <div
                            className={`mt-2 flex-shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all ${
                              isSelected
                                ? 'bg-blue-600 border-blue-600'
                                : 'bg-white border-slate-300 group-hover:border-blue-400'
                            }`}
                          >
                            {isSelected && <Check className="w-3 h-3 text-white" strokeWidth={3} />}
                          </div>

                          {/* Customer avatar */}
                          <div className="flex-shrink-0 w-9 h-9 rounded-xl border bg-blue-50 border-blue-200 text-blue-700 flex items-center justify-center text-sm font-bold">
                            {(receipt?.customer_name || 'P').trim().charAt(0).toUpperCase()}
                          </div>

                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between gap-2">
                              <h4 className="font-semibold text-slate-900 truncate flex-1 min-w-0">
                                {receipt?.customer_name || 'Partial Delivery'}
                              </h4>
                              <span className="flex-shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-blue-50 text-blue-700 border border-blue-200 whitespace-nowrap">
                                <GitBranch className="w-3 h-3" />
                                Partial · {itemCount} item{itemCount !== 1 ? 's' : ''}
                              </span>
                            </div>

                            <div className="flex items-center gap-1.5 text-sm mt-1 text-slate-600">
                              <MapPin className="w-3.5 h-3.5 flex-shrink-0 text-slate-400" />
                              <span className="truncate">
                                {receipt?.delivery_address || stop?.address || 'No address'}
                              </span>
                            </div>

                            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500 mt-1.5">
                              {receipt?.customer_phone && (
                                <span className="flex items-center gap-1">
                                  <Phone className="w-3 h-3" />
                                  {formatPhone(receipt.customer_phone)}
                                </span>
                              )}
                              <span className="text-blue-600 font-medium">
                                Split from a larger order — delivering selected items only
                              </span>
                            </div>

                            {/* Show items being delivered */}
                            {stop?.selected_items && stop.selected_items.length > 0 && (
                              <div className="mt-3 pt-3 border-t border-slate-100">
                                <p className="text-[11px] text-slate-500 mb-1.5">Items on this delivery</p>
                                <div className="flex flex-wrap gap-1.5">
                                  {stop.selected_items.map((item, idx) => (
                                    <span
                                      key={idx}
                                      className="px-2 py-1 rounded-md text-xs bg-slate-50 text-slate-700 border border-slate-200"
                                    >
                                      {item.item_name || item.sku}
                                      {item.color && <span className="text-slate-400 ml-1">({item.color})</span>}
                                      {item.quantity > 1 && <span className="text-slate-400 ml-1">×{item.quantity}</span>}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
                
                {/* Regular Ready Orders */}
                {readyOrders.map(receipt => (
                  <OrderCard key={receipt.id} receipt={receipt} selectable />
                ))}
              </div>
            )}
          </div>
        )}

        {/* Routes Section */}
        {activeSection === 'routes' && (
          <div className="space-y-4">
            {/* Routes Header with Filters and Cleanup - Hide on mobile since it's in sticky toolbar */}
            {!hideHeader && (
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
                {/* Filter Tabs */}
                <div className="flex items-center gap-1 p-1 bg-slate-50 rounded-xl">
                  <button
                    onClick={() => setRouteFilter('active')}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                      routeFilter === 'active'
                        ? 'bg-blue-100 text-blue-600 border border-blue-300'
                        : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
                    }`}
                  >
                    Active ({routes.filter(r => ['planned', 'dispatched', 'in_progress'].includes(r.status)).length})
                  </button>
                  <button
                    onClick={() => setRouteFilter('completed')}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                      routeFilter === 'completed'
                        ? 'bg-emerald-100 text-emerald-600 border border-emerald-300'
                        : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
                    }`}
                  >
                    Completed ({routes.filter(r => r.status === 'completed').length})
                  </button>
                  <button
                    onClick={() => setRouteFilter('all')}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                      routeFilter === 'all'
                        ? 'bg-blue-100 text-blue-600 border border-blue-300'
                        : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
                    }`}
                  >
                    All ({routes.length})
                  </button>
                </div>

                {/* Cleanup Button */}
                <button
                  onClick={() => setShowCleanupModal(true)}
                  className="flex items-center gap-2 px-3 py-1.5 bg-red-50 border border-red-200 rounded-lg text-red-600 text-xs font-medium hover:bg-red-100 transition-all"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                  Cleanup Routes
                </button>
              </div>
            )}

            {sortedRoutes.length === 0 ? (
              <div className="text-center py-16">
                <div className="w-16 h-16 rounded-2xl bg-slate-50 border border-slate-200 flex items-center justify-center mx-auto mb-4">
                  <Map className="w-8 h-8 text-gray-600" />
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">
                  {routeFilter === 'active' ? 'No Active Routes' : 
                   routeFilter === 'completed' ? 'No Completed Routes' : 'No Routes Yet'}
                </h3>
                <p className="text-slate-500 text-sm max-w-xs mx-auto mb-4">
                  {routeFilter === 'active' 
                    ? 'Create a route from ready orders to get started'
                    : routeFilter === 'completed'
                    ? 'Completed deliveries will appear here'
                    : 'Create a route from ready orders to get started'}
                </p>
                {routeFilter !== 'completed' && (
                  <button
                    onClick={() => setActiveSection('ready')}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-blue-100 border border-blue-300 rounded-lg text-blue-600 hover:bg-blue-100 transition-all"
                  >
                    <Plus className="w-4 h-4" />
                    View Ready Orders
                  </button>
                )}
              </div>
            ) : (
              <>
                {/* Routes Stats */}
                <div className="flex items-center gap-4 text-xs text-slate-500 mb-2">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-blue-400" />
                    <span>{routes.filter(r => r.status === 'planned').length} Planned</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-blue-400 animate-pulse" />
                    <span>{routes.filter(r => r.status === 'dispatched' || r.status === 'in_progress').length} In Progress</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-emerald-400" />
                    <span>{routes.filter(r => r.status === 'completed').length} Completed</span>
                  </div>
                </div>

                {/* Routes Grid */}
                <div className="grid gap-3 pb-36">
                  {sortedRoutes.map(route => (
                    <RouteCard key={route.id} route={route} />
                  ))}
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Floating Action Bar - Shows when orders are selected */}
      {(selectedOrders.length > 0 || selectedPartialRoutes.length > 0) && (
        <div 
          className="fixed bottom-20 md:bottom-0 left-0 right-0 z-50 p-3 md:p-4 bg-gradient-to-t from-slate-100 via-slate-100/95 to-transparent"
          style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
        >
          <div className="flex items-center justify-between gap-3 p-3 md:p-4 bg-white/95 backdrop-blur-xl border border-slate-200 rounded-2xl shadow-[0_12px_32px_-8px_rgba(15,23,42,0.25)]">
            <div className="flex items-center gap-2 md:gap-3 min-w-0">
              <div className="w-9 h-9 md:w-10 md:h-10 rounded-xl bg-blue-600 flex items-center justify-center flex-shrink-0 shadow-[0_4px_12px_-2px_rgba(37,99,235,0.40)]">
                <Truck className="w-4 h-4 md:w-5 md:h-5 text-white" />
              </div>
              <div className="min-w-0">
                <p className="text-slate-900 font-semibold text-sm md:text-base truncate">{selectedOrders.length + selectedPartialRoutes.length} stop{(selectedOrders.length + selectedPartialRoutes.length) !== 1 ? 's' : ''} selected</p>
                <p className="text-slate-500 text-xs hidden md:block">Next: we'll optimize the stop order and assign a driver</p>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <button
                onClick={() => { setSelectedOrders([]); setSelectedPartialRoutes([]); }}
                className="px-3 md:px-4 py-2 md:py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-slate-600 text-xs md:text-sm font-medium hover:bg-slate-100 transition-all active:scale-[0.98]"
              >
                Clear
              </button>
              <button
                onClick={() => {
                  optimizeRoute();
                  setShowCreateRouteModal(true);
                }}
                className="flex items-center gap-1.5 md:gap-2 px-3 md:px-5 py-2 md:py-2.5 bg-blue-600 rounded-xl text-white text-xs md:text-sm font-semibold hover:shadow-lg hover:shadow-blue-500/10 transition-all active:scale-[0.98]"
              >
                <Navigation className="w-3.5 h-3.5 md:w-4 md:h-4" />
                <span className="hidden sm:inline">Create</span> Route
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Route Drawer */}
      <Drawer
        open={showCreateRouteModal}
        onClose={() => setShowCreateRouteModal(false)}
        size="xl"
        zIndex={9999}
      >
              {/* Header */}
              <div className="p-5 border-b border-slate-200 flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-bold text-slate-900">Create Route</h3>
                  <p className="text-sm text-slate-500 mt-0.5">
                    {selectedOrders.length + selectedPartialRoutes.length} stop{(selectedOrders.length + selectedPartialRoutes.length) !== 1 ? 's' : ''} selected
                  </p>
                </div>
                <button
                  onClick={() => setShowCreateRouteModal(false)}
                  className="p-2 bg-slate-50 hover:bg-slate-100 border border-slate-200 rounded-xl text-slate-600 hover:text-slate-900 transition-all"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              {/* Content */}
              <div className="p-5 space-y-4">
                <div>
                  <label className="text-sm text-slate-600 mb-2 block">Route Name (optional)</label>
                  <input
                    type="text"
                    value={routeName}
                    onChange={(e) => setRouteName(e.target.value)}
                    placeholder="e.g., Thursday Morning Run"
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 placeholder-gray-600 focus:outline-none focus:border-blue-400 transition-all text-base"
                  />
                </div>

                <div>
                  <label className="text-sm text-slate-600 mb-3 block">Delivery Order</label>
                  
                  {/* Mode Toggle - AUTO vs MANUAL */}
                  <div className="flex gap-2 mb-4">
                    <button
                      onClick={() => {
                        setRouteOrderMode('auto');
                        if (optimizedOrder.length === 0) optimizeRoute();
                      }}
                      className={`flex-1 flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl text-sm font-medium transition-all ${
                        routeOrderMode === 'auto'
                          ? 'bg-gradient-to-r from-blue-50 to-slate-50 text-slate-900 border border-blue-300'
                          : 'bg-slate-50 text-slate-600 border border-slate-200 hover:bg-slate-100'
                      }`}
                    >
                      <Sparkles className="w-4 h-4" />
                      Auto-Optimize
                    </button>
                    <button
                      onClick={() => setRouteOrderMode('manual')}
                      className={`flex-1 flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl text-sm font-medium transition-all ${
                        routeOrderMode === 'manual'
                          ? 'bg-gradient-to-r from-blue-50 to-slate-50 text-slate-900 border border-blue-300'
                          : 'bg-slate-50 text-slate-600 border border-slate-200 hover:bg-slate-100'
                      }`}
                    >
                      <Hand className="w-4 h-4" />
                      Manual Order
                    </button>
                  </div>

                  {/* Mode Description */}
                  <div className={`text-xs px-3 py-2 rounded-lg mb-3 ${
                    routeOrderMode === 'auto' 
                      ? 'bg-blue-50 text-blue-600 border border-slate-200'
                      : 'bg-blue-50 text-blue-600 border border-blue-200'
                  }`}>
                    {routeOrderMode === 'auto' ? (
                      <div className="flex items-center gap-2">
                        <Navigation className="w-3.5 h-3.5" />
                        <span>Uses live traffic data to find the fastest route</span>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <ArrowUpDown className="w-3.5 h-3.5" />
                        <span>Drag stops to reorder or use arrow buttons</span>
                      </div>
                    )}
                  </div>

                  {/* Re-optimize button (auto mode only) */}
                  {routeOrderMode === 'auto' && (
                    <div className="flex justify-end mb-2">
                      <button
                        onClick={optimizeRoute}
                        disabled={optimizingRoute}
                        className="text-xs text-blue-600 hover:text-blue-600 flex items-center gap-1.5 px-2 py-1 bg-blue-50 rounded-lg transition-all"
                      >
                        {optimizingRoute ? (
                          <>
                            <Loader2 className="w-3 h-3 animate-spin" />
                            Optimizing...
                          </>
                        ) : (
                          <>
                            <RefreshCw className="w-3 h-3" />
                            Re-optimize
                          </>
                        )}
                      </button>
                    </div>
                  )}

                  {/* Stop List */}
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {routeOrderMode === 'auto' ? (
                      // Auto mode - read only list
                      (optimizedOrder.length > 0 ? optimizedOrder : selectedOrders).map((id, index) => {
                        const receipt = receipts.find(r => r.id === id);
                        return (
                          <div key={id} className="flex items-center gap-3 p-3 bg-slate-50 border border-slate-200 rounded-xl">
                            <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-blue-50 to-slate-50 border border-blue-300 flex items-center justify-center text-blue-600 text-sm font-bold flex-shrink-0">
                              {index + 1}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-slate-900 text-sm font-medium truncate">
                                {receipt?.customer_name}
                              </p>
                              <p className="text-slate-500 text-xs truncate">
                                {receipt?.delivery_address}
                              </p>
                            </div>
                          </div>
                        );
                      })
                    ) : (
                      // Manual mode - draggable list
                      manualOrder.map((id, index) => {
                        const receipt = receipts.find(r => r.id === id);
                        const isDragging = draggedIndex === index;
                        const isDragOver = dragOverIndex === index;
                        
                        return (
                          <div 
                            key={id}
                            draggable
                            onDragStart={(e) => handleDragStart(e, index)}
                            onDragEnter={(e) => handleDragEnter(e, index)}
                            onDragOver={handleDragOver}
                            onDragEnd={handleDragEnd}
                            className={`
                              flex items-center gap-2 p-3 bg-slate-50 border rounded-xl transition-all cursor-grab active:cursor-grabbing
                              ${isDragging ? 'opacity-50 border-blue-300' : 'border-slate-200'}
                              ${isDragOver ? 'border-blue-400 bg-blue-50' : ''}
                            `}
                          >
                            {/* Drag Handle */}
                            <div className="flex-shrink-0 touch-none">
                              <GripVertical className="w-4 h-4 text-slate-500" />
                            </div>
                            
                            {/* Stop Number */}
                            <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-blue-50 to-slate-50 border border-blue-300 flex items-center justify-center text-blue-600 text-sm font-bold flex-shrink-0">
                              {index + 1}
                            </div>
                            
                            {/* Stop Info */}
                            <div className="flex-1 min-w-0">
                              <p className="text-slate-900 text-sm font-medium truncate">
                                {receipt?.customer_name}
                              </p>
                              <p className="text-slate-500 text-xs truncate">
                                {receipt?.delivery_address}
                              </p>
                            </div>
                            
                            {/* Up/Down Buttons */}
                            <div className="flex flex-col gap-0.5 flex-shrink-0">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  moveStopUp(index);
                                }}
                                disabled={index === 0}
                                className="p-1 rounded bg-slate-50 hover:bg-slate-100 text-slate-600 hover:text-slate-900 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                              >
                                <ChevronRight className="w-3 h-3 -rotate-90" />
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  moveStopDown(index);
                                }}
                                disabled={index === manualOrder.length - 1}
                                className="p-1 rounded bg-slate-50 hover:bg-slate-100 text-slate-600 hover:text-slate-900 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                              >
                                <ChevronRight className="w-3 h-3 rotate-90" />
                              </button>
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="p-5 border-t border-slate-200 flex gap-3">
                <button
                  onClick={() => setShowCreateRouteModal(false)}
                  className="flex-1 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-600 font-medium hover:bg-slate-100 transition-all active:scale-[0.98]"
                >
                  Cancel
                </button>
                <button
                  onClick={createRoute}
                  disabled={creatingRoute}
                  className="flex-1 py-3 bg-blue-600 rounded-xl text-white font-semibold hover:shadow-lg hover:shadow-blue-500/10 transition-all disabled:opacity-50 flex items-center justify-center gap-2 active:scale-[0.98]"
                >
                  {creatingRoute ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    <>
                      <Plus className="w-4 h-4" />
                      Create Route
                    </>
                  )}
                </button>
              </div>
      </Drawer>

      {/* Dispatch Drawer */}
      <Drawer
        open={showDispatchModal && !!selectedRoute}
        onClose={() => { setShowDispatchModal(false); setSelectedRoute(null); }}
        size="xl"
        zIndex={9999}
      >
              {/* Header */}
              <div className="p-5 border-b border-slate-200 flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-bold text-slate-900">Dispatch Route</h3>
                  <p className="text-sm text-slate-500 mt-0.5">
                    Send route to driver via SMS
                  </p>
                </div>
                <button
                  onClick={() => { setShowDispatchModal(false); setSelectedRoute(null); }}
                  className="p-2 bg-slate-50 hover:bg-slate-100 border border-slate-200 rounded-xl text-slate-600 hover:text-slate-900 transition-all"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              {/* Content */}
              <div className="p-5 space-y-4">
                {savedDrivers.length > 0 && (
                  <div>
                    <label className="text-sm text-slate-600 mb-2 block">Saved Drivers</label>
                    <select
                      value={savedDrivers.find(d => normalizePhone(d.phone) === normalizePhone(driverInfo.phone))?.phone || ''}
                      onChange={(e) => {
                        const driver = savedDrivers.find(d => d.phone === e.target.value);
                        if (driver) {
                          setDriverInfo({ name: driver.name, phone: driver.phone, language: driver.language || 'en' });
                        }
                      }}
                      className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 focus:outline-none focus:border-blue-400 transition-all appearance-none cursor-pointer text-base"
                      style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%239ca3af'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 12px center', backgroundSize: '20px' }}
                    >
                      <option value="">Select a saved driver…</option>
                      {savedDrivers.map((d) => (
                        <option key={normalizePhone(d.phone)} value={d.phone}>
                          {d.name ? `${d.name} — ${formatPhoneDisplay(d.phone)}` : formatPhoneDisplay(d.phone)}
                        </option>
                      ))}
                    </select>
                    <p className="text-xs text-slate-400 mt-1.5">From past dispatches — or enter a new driver below</p>
                  </div>
                )}

                <div>
                  <label className="text-sm text-slate-600 mb-2 block">Driver Name</label>
                  <input
                    type="text"
                    value={driverInfo.name}
                    onChange={(e) => setDriverInfo(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="e.g., John"
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 placeholder-gray-600 focus:outline-none focus:border-blue-400 transition-all text-base"
                  />
                </div>

                <div>
                  <label className="text-sm text-slate-600 mb-2 block">Driver Phone <span className="text-blue-600">*</span></label>
                  <input
                    type="tel"
                    value={driverInfo.phone}
                    onChange={(e) => setDriverInfo(prev => ({ ...prev, phone: e.target.value }))}
                    placeholder="(555) 555-0100"
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 placeholder-gray-600 focus:outline-none focus:border-blue-400 transition-all text-base"
                  />
                </div>

                <div>
                  <label className="text-sm text-slate-600 mb-2 block">Driver Language</label>
                  <select
                    value={driverInfo.language}
                    onChange={(e) => setDriverInfo(prev => ({ ...prev, language: e.target.value }))}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 focus:outline-none focus:border-blue-400 transition-all appearance-none cursor-pointer text-base"
                    style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%239ca3af'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 12px center', backgroundSize: '20px' }}
                  >
                    {DRIVER_LANGUAGES.map(({ code, label, flag }) => (
                      <option key={code} value={code}>{flag} {label}</option>
                    ))}
                  </select>
                  <p className="text-xs text-slate-400 mt-1.5">SMS and driver app will use this language</p>
                </div>

                <div className="p-4 bg-blue-600/5 border border-slate-200 rounded-xl">
                  <p className="text-sm text-blue-600 flex items-start gap-2">
                    <MessageSquare className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span>Driver will receive an SMS with a link to view route details, navigation, and delivery instructions.</span>
                  </p>
                </div>
              </div>

              {/* Footer */}
              <div className="p-5 border-t border-slate-200 flex gap-3">
                <button
                  onClick={() => { setShowDispatchModal(false); setSelectedRoute(null); }}
                  className="flex-1 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-600 font-medium hover:bg-slate-100 transition-all active:scale-[0.98]"
                >
                  Cancel
                </button>
                <button
                  onClick={dispatchRoute}
                  disabled={!driverInfo.phone || dispatching}
                  className="flex-1 py-3 bg-blue-600 rounded-xl text-white font-semibold hover:shadow-lg hover:shadow-blue-500/10 transition-all disabled:opacity-50 flex items-center justify-center gap-2 active:scale-[0.98]"
                >
                  {dispatching ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      Send to Driver
                    </>
                  )}
                </button>
              </div>
      </Drawer>

      {/* Cleanup Routes Drawer */}
      <Drawer
        open={showCleanupModal}
        onClose={() => setShowCleanupModal(false)}
        size="xl"
        zIndex={50}
        closeOnBackdrop={!cleaningUp}
      >
            {/* Header */}
            <div className="p-5 border-b border-slate-200">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-red-100 border border-red-300 flex items-center justify-center">
                  <Trash2 className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-slate-900">Cleanup Routes</h3>
                  <p className="text-sm text-slate-500">Remove old or test routes</p>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="p-5 space-y-3">
              <p className="text-sm text-slate-600 mb-4">
                Select which routes to delete. This action cannot be undone.
              </p>

              {/* Cleanup Options */}
              <button
                onClick={() => cleanupOldRoutes('planned')}
                disabled={cleaningUp}
                className="w-full p-4 bg-slate-50 hover:bg-slate-100 border border-slate-200 rounded-xl text-left transition-all group disabled:opacity-50 active:scale-[0.99]"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-slate-900 font-medium">Planned Routes Only</p>
                    <p className="text-slate-500 text-sm">Routes that haven't been dispatched yet</p>
                  </div>
                  <div className="px-2 py-1 bg-blue-500/20 text-blue-400 text-xs font-medium rounded-lg">
                    {routes.filter(r => r.status === 'planned').length}
                  </div>
                </div>
              </button>

              <button
                onClick={() => cleanupOldRoutes('incomplete')}
                disabled={cleaningUp}
                className="w-full p-4 bg-slate-50 hover:bg-slate-100 border border-slate-200 rounded-xl text-left transition-all group disabled:opacity-50 active:scale-[0.99]"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-slate-900 font-medium">Incomplete Routes</p>
                    <p className="text-slate-500 text-sm">Planned + dispatched (not completed)</p>
                  </div>
                  <div className="px-2 py-1 bg-yellow-500/20 text-yellow-400 text-xs font-medium rounded-lg">
                    {routes.filter(r => ['planned', 'dispatched'].includes(r.status)).length}
                  </div>
                </div>
              </button>

              <button
                onClick={() => cleanupOldRoutes('completed')}
                disabled={cleaningUp}
                className="w-full p-4 bg-slate-50 hover:bg-slate-100 border border-slate-200 rounded-xl text-left transition-all group disabled:opacity-50 active:scale-[0.99]"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-slate-900 font-medium">Completed Routes</p>
                    <p className="text-slate-500 text-sm">Successfully delivered routes</p>
                  </div>
                  <div className="px-2 py-1 bg-emerald-100 text-emerald-600 text-xs font-medium rounded-lg">
                    {routes.filter(r => r.status === 'completed').length}
                  </div>
                </div>
              </button>

              <div className="border-t border-slate-200 pt-3 mt-3">
                <button
                  onClick={() => cleanupOldRoutes('all')}
                  disabled={cleaningUp}
                  className="w-full p-4 bg-red-50 hover:bg-red-100 border border-red-200 rounded-xl text-left transition-all disabled:opacity-50 active:scale-[0.99]"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-red-600 font-medium">Delete ALL Routes</p>
                      <p className="text-slate-500 text-sm">⚠️ This will delete everything</p>
                    </div>
                    <div className="px-2 py-1 bg-red-100 text-red-600 text-xs font-medium rounded-lg">
                      {routes.length}
                    </div>
                  </div>
                </button>
              </div>
            </div>

            {/* Footer */}
            <div className="p-5 border-t border-slate-200">
              {cleaningUp ? (
                <div className="flex items-center justify-center gap-2 text-slate-600">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Deleting routes...</span>
                </div>
              ) : (
                <button
                  onClick={() => setShowCleanupModal(false)}
                  className="w-full py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-600 font-medium hover:bg-slate-100 transition-all"
                >
                  Cancel
                </button>
              )}
            </div>
      </Drawer>

      {/* Driver Location Tracking Map */}
      {showDriverMap && (
        <DriverLocationMap
          storeId={storeId}
          onClose={() => setShowDriverMap(false)}
        />
      )}

      {/* Edit Route Order Drawer */}
      <Drawer
        open={showEditRouteModal && !!editingRoute}
        onClose={() => { setShowEditRouteModal(false); setEditingRoute(null); setEditingStops([]); }}
        size="xl"
        zIndex={9999}
      >
        {editingRoute && (
          <>
              {/* Header */}
              <div className="p-5 border-b border-slate-200 flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-bold text-slate-900">Edit Stop Order</h3>
                  <p className="text-sm text-slate-500 mt-0.5">
                    {editingRoute.route_name} • {editingStops.length} stops
                  </p>
                </div>
                <button
                  onClick={() => { setShowEditRouteModal(false); setEditingRoute(null); setEditingStops([]); }}
                  className="p-2 bg-slate-50 hover:bg-slate-100 border border-slate-200 rounded-xl text-slate-600 hover:text-slate-900 transition-all"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              {/* Content */}
              <div className="p-5 space-y-4">
                {/* Instructions */}
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-3">
                  <div className="flex items-center gap-2 text-blue-600 text-sm">
                    <ArrowUpDown className="w-4 h-4" />
                    <span>Drag stops to reorder or use the arrow buttons</span>
                  </div>
                </div>

                {/* Re-optimize button */}
                <div className="flex justify-end">
                  <button
                    onClick={reoptimizeRoute}
                    disabled={optimizingRoute}
                    className="text-xs text-blue-600 hover:text-blue-600 flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 rounded-lg transition-all"
                  >
                    {optimizingRoute ? (
                      <>
                        <Loader2 className="w-3 h-3 animate-spin" />
                        Optimizing...
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-3 h-3" />
                        Auto-Optimize
                      </>
                    )}
                  </button>
                </div>

                {/* Draggable Stop List */}
                <div className="space-y-2 max-h-80 overflow-y-auto">
                  {editingStops.map((stop, index) => {
                    const receipt = receipts.find(r => r.id === stop.receipt_id);
                    const isDragging = draggedIndex === index;
                    const isDragOver = dragOverIndex === index;
                    
                    return (
                      <div 
                        key={stop.id}
                        draggable
                        onDragStart={(e) => handleEditDragStart(e, index)}
                        onDragEnter={(e) => handleEditDragEnter(e, index)}
                        onDragOver={handleDragOver}
                        onDragEnd={handleEditDragEnd}
                        className={`
                          flex items-center gap-2 p-3 bg-slate-50 border rounded-xl transition-all cursor-grab active:cursor-grabbing
                          ${isDragging ? 'opacity-50 border-blue-300' : 'border-slate-200'}
                          ${isDragOver ? 'border-blue-400 bg-blue-50' : ''}
                        `}
                      >
                        {/* Drag Handle */}
                        <div className="flex-shrink-0 touch-none">
                          <GripVertical className="w-4 h-4 text-slate-500" />
                        </div>
                        
                        {/* Stop Number */}
                        <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-blue-50 to-slate-50 border border-blue-300 flex items-center justify-center text-blue-600 text-sm font-bold flex-shrink-0">
                          {index + 1}
                        </div>
                        
                        {/* Stop Info */}
                        <div className="flex-1 min-w-0">
                          <p className="text-slate-900 text-sm font-medium truncate">
                            {receipt?.customer_name || stop.customer_name || 'Customer'}
                          </p>
                          <p className="text-slate-500 text-xs truncate">
                            {stop.address || receipt?.delivery_address || 'No address'}
                          </p>
                        </div>
                        
                        {/* Up/Down Buttons */}
                        <div className="flex flex-col gap-0.5 flex-shrink-0">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              moveEditStopUp(index);
                            }}
                            disabled={index === 0}
                            className="p-1 rounded bg-slate-50 hover:bg-slate-100 text-slate-600 hover:text-slate-900 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                          >
                            <ChevronRight className="w-3 h-3 -rotate-90" />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              moveEditStopDown(index);
                            }}
                            disabled={index === editingStops.length - 1}
                            className="p-1 rounded bg-slate-50 hover:bg-slate-100 text-slate-600 hover:text-slate-900 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                          >
                            <ChevronRight className="w-3 h-3 rotate-90" />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Footer */}
              <div className="p-5 border-t border-slate-200 flex gap-3">
                <button
                  onClick={() => { setShowEditRouteModal(false); setEditingRoute(null); setEditingStops([]); }}
                  className="flex-1 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-600 font-medium hover:bg-slate-100 transition-all active:scale-[0.98]"
                >
                  Cancel
                </button>
                <button
                  onClick={saveRouteOrder}
                  disabled={savingRouteOrder}
                  className="flex-1 py-3 bg-blue-600 rounded-xl text-white font-semibold hover:shadow-lg hover:shadow-blue-500/10 transition-all disabled:opacity-50 flex items-center justify-center gap-2 active:scale-[0.98]"
                >
                  {savingRouteOrder ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Check className="w-4 h-4" />
                      Save Order
                    </>
                  )}
                </button>
              </div>
          </>
        )}
      </Drawer>
    </div>
  );
};

export default DeliveriesTab;